// Dashboard for report_calc_service + pdf_generation_service, via
// backend/api/generate_platform_report.js (real Lambdas by default, or the
// local docker-compose harness - see that repo's local/README.md - when the
// backend is running with PLATFORM_REPORT_BACKEND=local). See App.js /
// Sidebar.js for the REACT_APP_PLATFORM_REPORT_ENABLED gate this page sits
// behind.
//
// Charts mirror the same 11 charts pdf_generation renders into the PDF
// (origination, interest_vintage, interest_distribution, ticket_size, term,
// sector, purpose, credit_score, loss_vintage, recovery_vintage,
// repayment_status - see pdf_generation/chart_exporter.py), shown here as
// PrimeReact/Chart.js charts instead. The PDF itself is download-only, never
// shown inline.

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { TabView, TabPanel } from 'primereact/tabview';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputSwitch } from 'primereact/inputswitch';
import { InputNumber } from 'primereact/inputnumber';
import { SelectButton } from 'primereact/selectbutton';
import { Dialog } from 'primereact/dialog';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { SplitButton } from 'primereact/splitbutton';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import axiosWithAuth from '../../helpers/AxiosHelper';
import errorMessage from '../../helpers/errorMessage';
import { useAuth } from '../../hooks/useAuth';
import { useLocalStorage } from '../../helpers/useLocalStorage';
import { useTheme } from '../../hooks/useTheme';
import LendDocTabs from './LendReportSections';
import MozzenoDocTabs from './MozzenoReportSections';
import LoadingState from '../common/LoadingState';
import JobProgress from '../common/JobProgress';
import PageHeader from '../common/PageHeader';
import EmptyState from '../common/EmptyState';
import { CHART_COLORS, cyclePalette, BASE_OPTIONS, applyChartJsTheme, applyChartColorsTheme, chartRemountKey, formatLegendText } from './chart_theme';
import PdfLayoutGuide, { PdfSpot } from './PdfLayoutGuide';
import './platform_report.css';

// Platform list is no longer hardcoded here - it's whatever
// GET /generate-platform-report/platform-configs returns (the 4
// built-in platforms, auto-seeded server-side, plus anything onboarded via
// the "Onboard Platform" dialog below).

const YIELD_SOURCE_OPTIONS = [
    { label: 'Prefer origination (fallback to exaloan)', value: 'origination_preferred' },
    { label: 'Origination only', value: 'origination_only' },
    { label: 'Exaloan only', value: 'exaloan_only' },
];

const EMPTY_CALC_CONFIG = {
    use_aql_query: false,
    check_planned_payments: false,
    check_actual_payments: false,
    sector_column: 'sector_NACE_1_description',
    has_borrower_type: false,
    recovery_percent_default: 0.4,
    recovery_percent_consumer: 0.3,
    recovery_rules: [],
    use_loan_rating: false,
    yield_source: 'origination_preferred',
    data_rules: [],
    // null = this type has its own dedicated collection (today's only case
    // for every existing platform). {field, match, value} = this type shares
    // collection_name with another type; report_calc_service keeps only the
    // rows matching this before computing this type's own report - see
    // lambda_function.py's generate_report/_apply_type_filter.
    type_filter: null,
};

// Generic, per-loan data rules (exclude / status override) - see
// report_calc_service's apply_data_rules for how these are applied and the
// exact shape. field/match/action are closed enums by design (dropdowns
// only, no free text) - this stays purely declarative, never arbitrary code.
const RULE_FIELD_OPTIONS = [
    { label: 'Loan ID', value: 'source_platform_loan_id' },
    { label: 'Borrower ID', value: 'source_platform_borrower_id' },
];
const RULE_MATCH_OPTIONS = [
    { label: 'equals', value: 'equals' },
    { label: 'is one of (comma-separated)', value: 'in' },
    { label: 'starts with', value: 'starts_with' },
];
const RULE_ACTION_OPTIONS = [
    { label: 'Exclude from report', value: 'exclude' },
    { label: 'Set status', value: 'set_status' },
];
const RULE_STATUS_OPTIONS = [
    { label: 'Defaulted', value: 'defaulted' },
    { label: 'Completed', value: 'completed' },
    { label: 'Prepaid', value: 'prepaid' },
];
function emptyDataRule() {
    return { field: 'source_platform_loan_id', match: 'equals', value: '', action: 'exclude', params: {} };
}

// Recovery rules: per-loan / per-borrower recovery rate overrides or exclusions.
// Unlike data_rules (which remove/relabel loans), these only affect the
// expected-recovery multiplier inside calculate_valuation - the loan stays in
// the report. field/match match data_rules' closed enums; action is either
// "exclude" (rate → 0) or "override" (rate → recovery_rate).
const RECOVERY_RULE_ACTION_OPTIONS = [
    { label: 'Exclude from recovery (rate = 0)', value: 'exclude' },
    { label: 'Override recovery rate', value: 'override' },
];
function emptyRecoveryRule() {
    return { field: 'source_platform_loan_id', match: 'equals', value: '', action: 'exclude', recovery_rate: null };
}

function RecoveryRuleRow({ rule, onChange, onRemove }) {
    const valueAsText = Array.isArray(rule.value) ? rule.value.join(', ') : (rule.value || '');
    const setValue = (text) =>
        onChange({ ...rule, value: rule.match === 'in' ? text.split(',').map((s) => s.trim()).filter(Boolean) : text });
    return (
        <div className="p-fluid grid align-items-end mb-2 p-2 pr-rule-box">
            <div className="field col-2 mb-0">
                <label className="text-sm">Field</label>
                <Dropdown value={rule.field} options={RULE_FIELD_OPTIONS} onChange={(e) => onChange({ ...rule, field: e.value })} />
            </div>
            <div className="field col-2 mb-0">
                <label className="text-sm">Match</label>
                <Dropdown
                    value={rule.match}
                    options={RULE_MATCH_OPTIONS}
                    onChange={(e) => {
                        const becomingList = e.value === 'in';
                        const wasList = Array.isArray(rule.value);
                        let nextValue = rule.value;
                        if (becomingList && !wasList) nextValue = rule.value ? [rule.value] : [];
                        else if (!becomingList && wasList) nextValue = rule.value[0] || '';
                        onChange({ ...rule, match: e.value, value: nextValue });
                    }}
                />
            </div>
            <div className="field col-3 mb-0">
                <label className="text-sm">Value{rule.match === 'in' ? ' (comma-separated)' : ''}</label>
                <InputText value={valueAsText} onChange={(e) => setValue(e.target.value)} />
            </div>
            <div className="field col-2 mb-0">
                <label className="text-sm">Action</label>
                <Dropdown
                    value={rule.action}
                    options={RECOVERY_RULE_ACTION_OPTIONS}
                    onChange={(e) => onChange({ ...rule, action: e.value, recovery_rate: e.value === 'override' ? (rule.recovery_rate ?? 0.4) : null })}
                />
            </div>
            {rule.action === 'override' && (
                <div className="field col-2 mb-0">
                    <label className="text-sm">Recovery rate (0–1)</label>
                    <InputNumber
                        value={rule.recovery_rate}
                        onValueChange={(e) => onChange({ ...rule, recovery_rate: e.value })}
                        mode="decimal"
                        maxFractionDigits={2}
                        min={0}
                        max={1}
                        step={0.05}
                        showButtons
                    />
                </div>
            )}
            <div className="field col-1 mb-0 flex align-items-end">
                <Button icon="pi pi-trash" className="p-button-danger p-button-text" onClick={onRemove} />
            </div>
        </div>
    );
}

// One rule row - value is a single string for "equals"/"starts_with", a
// comma-separated list (parsed to an array on save) for "in". Extra
// status/date inputs only shown for the "set_status" action.
function DataRuleRow({ rule, onChange, onRemove }) {
    const valueAsText = Array.isArray(rule.value) ? rule.value.join(', ') : (rule.value || '');
    const setValue = (text) => onChange({ ...rule, value: rule.match === 'in' ? text.split(',').map((s) => s.trim()).filter(Boolean) : text });
    return (
        <div className="p-fluid grid align-items-end mb-2 p-2 pr-rule-box">
            <div className="field col-2 mb-0">
                <label className="text-sm">Field</label>
                <Dropdown value={rule.field} options={RULE_FIELD_OPTIONS} onChange={(e) => onChange({ ...rule, field: e.value })} />
            </div>
            <div className="field col-2 mb-0">
                <label className="text-sm">Match</label>
                <Dropdown
                    value={rule.match}
                    options={RULE_MATCH_OPTIONS}
                    onChange={(e) => {
                        const becomingList = e.value === 'in';
                        const wasList = Array.isArray(rule.value);
                        let nextValue = rule.value;
                        if (becomingList && !wasList) nextValue = rule.value ? [rule.value] : [];
                        else if (!becomingList && wasList) nextValue = rule.value[0] || '';
                        onChange({ ...rule, match: e.value, value: nextValue });
                    }}
                />
            </div>
            <div className="field col-3 mb-0">
                <label className="text-sm">Value{rule.match === 'in' ? ' (comma-separated)' : ''}</label>
                <InputText value={valueAsText} onChange={(e) => setValue(e.target.value)} />
            </div>
            <div className="field col-2 mb-0">
                <label className="text-sm">Action</label>
                <Dropdown value={rule.action} options={RULE_ACTION_OPTIONS} onChange={(e) => onChange({ ...rule, action: e.value, params: e.value === 'set_status' ? (rule.params || {}) : {} })} />
            </div>
            {rule.action === 'set_status' && (
                <>
                    <div className="field col-2 mb-0">
                        <label className="text-sm">New status</label>
                        <Dropdown value={rule.params?.status || null} options={RULE_STATUS_OPTIONS} onChange={(e) => onChange({ ...rule, params: { ...rule.params, status: e.value } })} placeholder="Select" />
                    </div>
                    <div className="field col-2 mb-0">
                        <label className="text-sm">Default date (optional)</label>
                        <Calendar value={rule.params?.default_date ? new Date(rule.params.default_date) : null} onChange={(e) => onChange({ ...rule, params: { ...rule.params, default_date: e.value ? toIsoDate(e.value) : undefined } })} dateFormat="yy-mm-dd" />
                    </div>
                    <div className="field col-2 mb-0">
                        <label className="text-sm">Don't override if already</label>
                        <Dropdown
                            value={rule.params?.skip_if_status?.[0] || null}
                            options={RULE_STATUS_OPTIONS}
                            showClear
                            placeholder="(none)"
                            onChange={(e) => onChange({ ...rule, params: { ...rule.params, skip_if_status: e.value ? [e.value] : undefined } })}
                        />
                    </div>
                </>
            )}
            <div className="col-1 mb-0 flex justify-content-end">
                <Button icon="pi pi-trash" className="p-button-text p-button-danger" onClick={onRemove} />
            </div>
        </div>
    );
}

const EMPTY_PDF_CONFIG = {
    display_name: '',
    currency: 'EUR',
    currency_symbol: '€',
    color_primary: '#0f1942',
    color_secondary: '#2d5f8a',
    color_accent: '#4a90c4',
    has_sectors: true,
    has_purposes: false,
    has_credit_scores: false,
    has_repayment_behavior: false,
    logo_file: '',
    report_country: '',
    report_loan_types: '',
    report_credit_insurance: 'No',
    report_operating_model: '',
    report_label: 'Loan Report',
    report_description: '',
};

// calc_config's two yield-source booleans collapse into one SelectButton in
// the form; convert both ways so editing an existing platform round-trips
// correctly (see the identical three-way logic in lambda_function.py's
// calculate_distributions).
function calcConfigToForm(calc_config) {
    let yield_source = 'origination_preferred';
    if (calc_config?.gross_yield_exaloan_only) yield_source = 'exaloan_only';
    else if (calc_config?.gross_yield_at_origination_only) yield_source = 'origination_only';
    return { ...EMPTY_CALC_CONFIG, ...(calc_config || {}), yield_source };
}

function formToCalcConfig(form) {
    const { yield_source, ...rest } = form;
    return {
        ...rest,
        gross_yield_exaloan_only: yield_source === 'exaloan_only',
        gross_yield_at_origination_only: yield_source === 'origination_only',
    };
}

function emptySegment() {
    return { name: '', label: '', collection_name: '', calc: EMPTY_CALC_CONFIG, pdf: EMPTY_PDF_CONFIG };
}

function segmentToForm(seg) {
    return {
        name: seg.name || '',
        label: seg.label || '',
        collection_name: seg.collection_name || '',
        calc: calcConfigToForm(seg.calc_config),
        pdf: { ...EMPTY_PDF_CONFIG, ...(seg.pdf_config || {}) },
    };
}

// One segment's calc+PDF fields - identical field set whether the platform
// has 1 segment (fields shown standalone, name/label/collection hidden and
// implied by the platform itself) or several (each gets its own named,
// collapsible-looking card - see OnboardPlatformDialog).
// One borrower type: its label (shown in the PDF), which optional chart
// sections its PDF shows, and - folded away under "Advanced" - how its loans
// are calculated. `defaults.collection`: the collection used when the
// override is left empty.
function SegmentFieldSet({ segment, onChange, showNameFields, lockedName = null, hideCalcConfig = false, defaults = {} }) {
    const calc = segment.calc;
    const pdf = segment.pdf;
    const setCalc = (next) => onChange({ ...segment, calc: next });
    const setPdf = (next) => onChange({ ...segment, pdf: next });

    return (
        <div className="p-fluid grid">
            {showNameFields && (
                <>
                    <div className="field col-6">
                        <label>
                            Borrower type label * <PdfSpot spot="borrowerType" />
                        </label>
                        <InputText value={segment.label} onChange={(e) => onChange({ ...segment, label: e.target.value })} placeholder="e.g. SME" />
                        <small className="text-color-secondary">Shown in the PDF as the loan type and as this section&apos;s name.</small>
                    </div>
                    {/* A single type's internal name is the Originator slug
                        (handleSave), so there's nothing to fill in. */}
                    {!lockedName && (
                        <div className="field col-6">
                            <label>Internal name *</label>
                            <InputText value={segment.name} onChange={(e) => onChange({ ...segment, name: e.target.value.toLowerCase() })} placeholder="e.g. consumer" />
                            <small className="text-color-secondary">Lowercase letters, digits and underscores - used for the data, not shown in the PDF.</small>
                        </div>
                    )}
                </>
            )}

            <div className="col-12">
                <h5 className="pr-form-section-title mb-1 mt-2">Show in the PDF</h5>
            </div>
            <div className="field col-6 flex align-items-center gap-2">
                <InputSwitch checked={pdf.has_purposes} onChange={(e) => setPdf({ ...pdf, has_purposes: e.value })} />
                <label className="m-0">
                    Loans by purpose <PdfSpot spot="purposes" />
                </label>
            </div>
            <div className="field col-6 flex align-items-center gap-2">
                <InputSwitch checked={pdf.has_sectors} onChange={(e) => setPdf({ ...pdf, has_sectors: e.value })} />
                <label className="m-0">
                    Loans by sector <PdfSpot spot="sectors" />
                </label>
            </div>
            <div className="field col-6 flex align-items-center gap-2">
                <InputSwitch checked={pdf.has_credit_scores} onChange={(e) => setPdf({ ...pdf, has_credit_scores: e.value })} />
                <label className="m-0">
                    Originator Score charts and table <PdfSpot spot="creditScores" />
                </label>
            </div>
            <div className="field col-6 flex align-items-center gap-2">
                <InputSwitch checked={pdf.has_repayment_behavior} onChange={(e) => setPdf({ ...pdf, has_repayment_behavior: e.value })} />
                <label className="m-0">
                    Repayment status by vintage <PdfSpot spot="repayment" />
                </label>
            </div>

            {!hideCalcConfig && (
                <div className="col-12">
                    <Accordion className="pr-advanced-accordion">
                        <AccordionTab header="Advanced: calculation settings">
                            <div className="p-fluid grid">
                                <div className="field col-6">
                                    <label>Collection override (optional)</label>
                                    <InputText
                                        value={segment.collection_name}
                                        onChange={(e) => onChange({ ...segment, collection_name: e.target.value })}
                                        placeholder={defaults.collection}
                                    />
                                    <small className="text-color-secondary">Empty: {defaults.collection}</small>
                                </div>
            <div className="col-12">
                <h5 className="pr-form-section-title mb-1">Calculation config</h5>
                <div className="text-color-secondary text-sm mb-2">How this borrower type's own loans are computed - filters, sector column, recovery assumptions.</div>
            </div>
            <div className="field col-6 flex align-items-center gap-2">
                <InputSwitch checked={calc.check_planned_payments} onChange={(e) => setCalc({ ...calc, check_planned_payments: e.value })} />
                <label className="m-0">Require planned payments</label>
            </div>
            <div className="field col-6 flex align-items-center gap-2">
                <InputSwitch checked={calc.check_actual_payments} onChange={(e) => setCalc({ ...calc, check_actual_payments: e.value })} />
                <label className="m-0">Require actual payments</label>
            </div>
            <div className="field col-6 flex align-items-center gap-2">
                <InputSwitch checked={calc.use_aql_query} onChange={(e) => setCalc({ ...calc, use_aql_query: e.value })} />
                <label className="m-0">Filter by report_date via AQL (large collections)</label>
            </div>
            <div className="field col-6 flex align-items-center gap-2">
                <InputSwitch checked={calc.use_loan_rating} onChange={(e) => setCalc({ ...calc, use_loan_rating: e.value })} />
                <label className="m-0">Uses loan rating</label>
            </div>
            <div className="field col-12">
                <label>Sector column</label>
                <InputText value={calc.sector_column} onChange={(e) => setCalc({ ...calc, sector_column: e.target.value })} />
            </div>

            <div className="col-12 mt-2">
                <div className="flex align-items-center gap-2 mb-2">
                    <InputSwitch checked={calc.has_borrower_type} onChange={(e) => setCalc({ ...calc, has_borrower_type: e.value })} />
                    <label className="m-0">Use a different recovery % for SME rows within this one collection</label>
                </div>
                {/* Not the same thing as onboarding 2+ borrower types (above) -
                    that splits into separate borrower-type sections entirely
                    (own collection or type_filter). This is narrower: it only
                    changes which recovery % applies, for a borrower type that
                    is NOT being split out on its own. Leave off if this type's
                    recovery % is uniform, or if SME/consumer are already
                    separate borrower types elsewhere in this form. */}
                <div className="text-color-secondary text-sm mb-2">
                    Only relevant if this collection mixes SME and consumer loans that you're <em>not</em> splitting into separate borrower types - it changes the recovery % used for SME rows specifically, nothing else.
                </div>
                <div className="p-fluid grid">
                    <div className={calc.has_borrower_type ? 'field col-6' : 'field col-12'}>
                        {/* calculate_valuation (lambda_function.py) applies
                            recovery_percent_default to SME rows specifically
                            when has_borrower_type is on - "default" here means
                            "SME's rate", not "everyone else's" - only when
                            has_borrower_type is off does it mean the one
                            overall rate. Label reflects whichever is true. */}
                        <label>{calc.has_borrower_type ? 'Recovery % (SME rows)' : 'Recovery %'}</label>
                        <InputNumber value={calc.recovery_percent_default} onValueChange={(e) => setCalc({ ...calc, recovery_percent_default: e.value })} mode="decimal" maxFractionDigits={2} min={0} max={1} step={0.05} showButtons />
                    </div>
                    {calc.has_borrower_type && (
                        <div className="field col-6">
                            <label>Recovery % (everyone else)</label>
                            <InputNumber value={calc.recovery_percent_consumer} onValueChange={(e) => setCalc({ ...calc, recovery_percent_consumer: e.value })} mode="decimal" maxFractionDigits={2} min={0} max={1} step={0.05} showButtons />
                        </div>
                    )}
                </div>
            </div>

            <div className="field col-12">
                <label className="block mb-2">Gross yield source</label>
                <SelectButton value={calc.yield_source} options={YIELD_SOURCE_OPTIONS} onChange={(e) => e.value && setCalc({ ...calc, yield_source: e.value })} />
            </div>

            {showNameFields && (
                <div className="col-12">
                    <div className="flex align-items-center gap-2 mb-2">
                        <InputSwitch
                            checked={Boolean(calc.type_filter)}
                            onChange={(e) => setCalc({ ...calc, type_filter: e.value ? { field: '', match: 'equals', value: '' } : null })}
                        />
                        <label className="m-0">This borrower type shares its collection with another type</label>
                    </div>
                    {calc.type_filter && (
                        <div className="p-fluid grid align-items-end mb-2 p-2 pr-rule-box">
                            <div className="field col-4 mb-0">
                                <label className="text-sm">Column name</label>
                                <InputText
                                    value={calc.type_filter.field}
                                    onChange={(e) => setCalc({ ...calc, type_filter: { ...calc.type_filter, field: e.target.value } })}
                                    placeholder="e.g. loan_type"
                                />
                            </div>
                            <div className="field col-3 mb-0">
                                <label className="text-sm">Match</label>
                                <Dropdown
                                    value={calc.type_filter.match}
                                    options={RULE_MATCH_OPTIONS}
                                    onChange={(e) => {
                                        const becomingList = e.value === 'in';
                                        const wasList = Array.isArray(calc.type_filter.value);
                                        let nextValue = calc.type_filter.value;
                                        if (becomingList && !wasList) nextValue = calc.type_filter.value ? [calc.type_filter.value] : [];
                                        else if (!becomingList && wasList) nextValue = calc.type_filter.value[0] || '';
                                        setCalc({ ...calc, type_filter: { ...calc.type_filter, match: e.value, value: nextValue } });
                                    }}
                                />
                            </div>
                            <div className="field col-5 mb-0">
                                <label className="text-sm">Value{calc.type_filter.match === 'in' ? ' (comma-separated)' : ''}</label>
                                <InputText
                                    value={Array.isArray(calc.type_filter.value) ? calc.type_filter.value.join(', ') : calc.type_filter.value || ''}
                                    onChange={(e) => {
                                        const text = e.target.value;
                                        setCalc({
                                            ...calc,
                                            type_filter: { ...calc.type_filter, value: calc.type_filter.match === 'in' ? text.split(',').map((s) => s.trim()).filter(Boolean) : text },
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="col-12">
                <h5 className="pr-form-section-title mb-1">Recovery rules (per-loan / per-borrower overrides)</h5>
                <div className="text-color-secondary text-sm mb-2">
                    Optional. Override or zero out the recovery rate for specific loans or borrowers — without removing them from the report.
                    Use "Exclude from recovery" to set rate = 0 for a loan you expect no recovery on; use "Override recovery rate" to set a
                    custom rate. Rules are applied after the global recovery % above.
                </div>
                {(calc.recovery_rules || []).map((rule, i) => (
                    <RecoveryRuleRow
                        key={i}
                        rule={rule}
                        onChange={(next) => {
                            const rules = [...calc.recovery_rules];
                            rules[i] = next;
                            setCalc({ ...calc, recovery_rules: rules });
                        }}
                        onRemove={() => setCalc({ ...calc, recovery_rules: calc.recovery_rules.filter((_, j) => j !== i) })}
                    />
                ))}
                <Button
                    label="+ Add Recovery Rule"
                    className="p-button-text p-button-sm"
                    onClick={() => setCalc({ ...calc, recovery_rules: [...(calc.recovery_rules || []), emptyRecoveryRule()] })}
                />
            </div>

            <div className="col-12">
                <h5 className="pr-form-section-title mb-1">Data rules (loan exclusions / status overrides)</h5>
                <div className="text-color-secondary text-sm mb-2">Optional, per-loan overrides - e.g. exclude a specific loan, or force a borrower's loans to "defaulted". Leave empty if none apply.</div>
                {(calc.data_rules || []).map((rule, i) => (
                    <DataRuleRow
                        key={i}
                        rule={rule}
                        onChange={(next) => {
                            const rules = [...calc.data_rules];
                            rules[i] = next;
                            setCalc({ ...calc, data_rules: rules });
                        }}
                        onRemove={() => setCalc({ ...calc, data_rules: calc.data_rules.filter((_, j) => j !== i) })}
                    />
                ))}
                <Button
                    label="+ Add Rule"
                    className="p-button-text p-button-sm"
                    onClick={() => setCalc({ ...calc, data_rules: [...(calc.data_rules || []), emptyDataRule()] })}
                />
            </div>
                            </div>
                        </AccordionTab>
                    </Accordion>
                </div>
            )}
        </div>
    );
}

// Branding shared by every borrower type of a multi-type platform - the
// platform-level counterpart to each type's own (trimmed) pdf_config, see
// SegmentFieldSet's "PDF report config" section and handleSave below.
const DEFAULT_TOP_PDF = {
    currency: 'EUR',
    currency_symbol: '€',
    color_primary: '#0f1942',
    color_secondary: '#2d5f8a',
    color_accent: '#4a90c4',
    logo_file: '',
    logo_s3_key: '',
    report_country: '',
    report_credit_insurance: 'No',
    report_operating_model: '',
    report_description: '',
};
// Keys collected once on topPdf for a multi-type platform, then layered onto
// every type's own pdf_config at save time (handleSave) so each persisted
// segment still ends up with the full field set pdf_generation_service and
// the dashboard's logo/branding fallbacks already expect - nothing
// downstream needs to change, only the onboarding form stops asking for
// these once per type.
const SHARED_PDF_KEYS = [
    'currency',
    'currency_symbol',
    'color_primary',
    'color_secondary',
    'color_accent',
    'logo_file',
    'logo_s3_key',
    'report_country',
    'report_credit_insurance',
    'report_operating_model',
];

// Doubles as both "onboard a brand-new platform" and "edit an existing
// one's config" (including a built-in) - one form, one endpoint. A platform
// has 1+ borrower types (segments internally - each an independent data
// source/config); the form looks and behaves identically whether there's 1
// (e.g. "anodu") or several (e.g. "finbee" = consumer + sme) - same
// "Platform branding" section, same Accordion of borrower-type cards, same
// fields per type. The only difference for exactly 1 type: its internal
// name is locked to the platform slug (see SegmentFieldSet's lockedName
// prop) rather than freely editable, since report_calc_service's real
// PLATFORM_CONFIG keys/collection naming depend on it matching. Add a 2nd+
// type to unlock free per-type naming - the dashboard/PDF then always show
// the combined Overview recomputed across every type.
function OnboardPlatformDialog({ visible, onHide, platformConfigs, onSaved, toast }) {
    const [selectedExisting, setSelectedExisting] = useState(null);
    const [slug, setSlug] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [topPdf, setTopPdf] = useState(DEFAULT_TOP_PDF);
    const [segments, setSegments] = useState([emptySegment()]);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
    const [logoUploading, setLogoUploading] = useState(false);
    // "Special calculation" = this platform's report data comes from an
    // existing, separately-populated collection (Lend's
    // lend_performance_report_2 is the one example today) rather than this
    // app's own report_calc_service calc step - see backend's
    // resolveTarget/upsertPlatformDoc "source" field. Everything else about
    // onboarding (branding, logo upload, borrower types) works identically
    // either way; only the calc-config section (SegmentFieldSet's
    // hideCalcConfig) is skipped for a special-calculation platform, since
    // there's genuinely nothing for it to configure.
    const [isSpecialCalc, setIsSpecialCalc] = useState(false);
    const [sourceCollection, setSourceCollection] = useState('');

    useEffect(() => {
        if (!visible) return;
        setSelectedExisting(null);
        setSlug('');
        setDisplayName('');
        setTopPdf(DEFAULT_TOP_PDF);
        setSegments([emptySegment()]);
        setFormError(null);
        setIsSpecialCalc(false);
        setSourceCollection('');
    }, [visible]);

    const existingOptions = platformConfigs.map((p) => ({ label: p.display_name || p.platform, value: p.platform }));
    const isMulti = segments.length > 1;

    const handleSelectExisting = (value) => {
        setSelectedExisting(value);
        setLogoPreviewUrl(null);
        if (!value) {
            setSlug('');
            setDisplayName('');
            setTopPdf(DEFAULT_TOP_PDF);
            setSegments([emptySegment()]);
            setIsSpecialCalc(false);
            setSourceCollection('');
            return;
        }
        const cfg = platformConfigs.find((p) => p.platform === value);
        setSlug(value);
        setDisplayName(cfg?.display_name || '');
        setTopPdf({ ...DEFAULT_TOP_PDF, ...(cfg?.pdf_config || {}) });
        setSegments((cfg?.segments?.length ? cfg.segments : [{}]).map(segmentToForm));
        setIsSpecialCalc(Boolean(cfg?.source));
        setSourceCollection(cfg?.source || '');
        if (cfg?.pdf_config?.logo_s3_key) {
            axiosWithAuth.get(`/generate-platform-report/platform-configs/${value}/logo-url`)
                .then((res) => setLogoPreviewUrl(res.data.logo_url))
                .catch(() => setLogoPreviewUrl(null));
        }
    };

    // Uploads immediately on file selection rather than deferring to the
    // main Save button - the logo is its own S3 object, not a form field
    // that gets bundled into the JSON pdf_config POST, so there's no "unsaved
    // logo" state to lose if the admin closes the dialog without hitting Save.
    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !selectedExisting) return;
        setLogoUploading(true);
        try {
            const formData = new FormData();
            formData.append('logo', file);
            const res = await axiosWithAuth.post(
                `/generate-platform-report/platform-configs/${selectedExisting}/logo`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } },
            );
            setTopPdf((prev) => ({ ...prev, logo_s3_key: res.data.logo_s3_key }));
            setLogoPreviewUrl(res.data.logo_url);
        } catch (err) {
            setFormError('Failed to upload logo: ' + errorMessage(err));
        } finally {
            setLogoUploading(false);
            e.target.value = '';
        }
    };

    const updateSegment = (index, updated) => setSegments(segments.map((s, i) => (i === index ? updated : s)));
    const addSegment = () => setSegments([...segments, emptySegment()]);
    const removeSegment = (index) => setSegments(segments.filter((_, i) => i !== index));

    const handleSave = async () => {
        setFormError(null);
        const isNew = !selectedExisting;
        const finalSlug = selectedExisting || slug.trim().toLowerCase();
        if (isNew && !/^[a-z0-9_]+$/.test(finalSlug)) {
            setFormError('Originator slug must be lowercase letters, digits, and underscores only.');
            return;
        }
        if (isNew && platformConfigs.some((p) => p.platform === finalSlug)) {
            setFormError(`'${finalSlug}' is already onboarded - select it from the dropdown above to edit its config instead.`);
            return;
        }
        // Every type's own pdf_config still ends up carrying the full field
        // set (branding from topPdf + this type's own display_name/
        // loan_types/report_label/has_*/description) - the form only asks
        // for branding once, in "Platform branding" above; the persisted
        // document shape, and everything downstream that reads a segment's
        // pdf_config, is unchanged. A single-type platform's one type keeps
        // its name locked to the platform slug (finalSlug) - see
        // SegmentFieldSet's lockedName prop below - so it stays exactly
        // equivalent to a real report_calc_service PLATFORM_CONFIG key,
        // same as before this form always showed the name field.
        // Every platform has at least one real borrower type (e.g. "SME") -
        // even a single-segment platform is never just "itself" as a type,
        // so the label is required rather than silently falling back to the
        // platform's own display name/slug (that fallback used to be exactly
        // how e.g. Anodu/Fulfin/Flexfunding ended up showing their own
        // platform name as their "borrower type" everywhere in the app,
        // instead of the real one, "SME").
        if (segments.some((seg) => !seg.label.trim())) {
            setFormError('Every borrower type needs a label (e.g. "SME", "Consumer") - it can\'t be left blank.');
            return;
        }
        if (isSpecialCalc && !sourceCollection.trim()) {
            setFormError('Special calculation needs the existing collection name it reads from (e.g. "lend_performance_report_2").');
            return;
        }
        // SHARED_PDF_KEYS (colors, currency, logo, ...) live once on the
        // platform's own top-level pdf_config (saved as `pdf_config: topPdf`
        // below) - stripped out of each segment's own pdf_config here rather
        // than copied in, so they're not persisted redundantly per segment.
        // buildPdfPayload (generate_platform_report.js) merges the two back
        // together at PDF-request time, so pdf_generation_service still
        // receives the same complete field set as before - nothing there
        // needs to change. A special-calculation platform's segments get an
        // empty calc_config - there's no calc step to configure, and the
        // form's own calc fields are hidden for it (SegmentFieldSet's
        // hideCalcConfig), so formToCalcConfig's defaults would otherwise be
        // persisted meaninglessly.
        const segmentPayload = segments.map((seg) => ({
            name: (isMulti ? seg.name : finalSlug).trim().toLowerCase(),
            label: seg.label.trim(),
            collection_name: isSpecialCalc ? null : (seg.collection_name || undefined),
            calc_config: isSpecialCalc ? {} : formToCalcConfig(seg.calc),
            pdf_config: Object.fromEntries(Object.entries(seg.pdf).filter(([k]) => !SHARED_PDF_KEYS.includes(k))),
        }));
        if (isMulti) {
            const names = segmentPayload.map((s) => s.name);
            if (names.some((n) => !/^[a-z0-9_]+$/.test(n))) {
                setFormError('Every borrower type needs a name (lowercase letters, digits, underscores only).');
                return;
            }
            if (new Set(names).size !== names.length) {
                setFormError('Borrower type names must be unique within this Originator.');
                return;
            }
        }
        setSaving(true);
        try {
            await axiosWithAuth.post('/generate-platform-report/platform-configs', {
                platform: finalSlug,
                display_name: displayName || finalSlug,
                pdf_config: topPdf,
                segments: segmentPayload,
                is_new: isNew,
                // Always sent (never omitted) so unchecking "Special
                // calculation" on an edit explicitly clears a previously-set
                // source back to a normal calc-driven platform, rather than
                // the backend's "not sent at all -> leave alone" default
                // silently keeping the old value.
                source: isSpecialCalc ? sourceCollection.trim() : '',
            });
            toast.current?.show({
                severity: 'success',
                summary: selectedExisting ? 'Originator updated' : 'Originator onboarded',
                detail: isMulti ? `${finalSlug} (${segmentPayload.length} segments)` : `${finalSlug} - raw loanbook: mapped/${segmentPayload[0].name}_mapped`,
                life: 6000,
            });
            onSaved(finalSlug);
            onHide();
        } catch (err) {
            setFormError(errorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const dialogFooter = (
        <div className="pr-dialog-footer">
            {formError && <div className="ex-inline-error">{formError}</div>}
            <div className="flex justify-content-end gap-2">
                <Button label="Cancel" className="p-button-text" onClick={onHide} disabled={saving} />
                <Button label={selectedExisting ? 'Save changes' : 'Onboard Originator'} icon="pi pi-check" onClick={handleSave} loading={saving} />
            </div>
        </div>
    );

    return (
        <Dialog
            header="Onboard / Edit Originator"
            visible={visible}
            onHide={onHide}
            style={{ width: '58rem' }}
            breakpoints={{ '1100px': '94vw' }}
            className="pr-onboard-dialog"
            footer={dialogFooter}
            modal
        >
            <div className="p-fluid grid mb-2">
                <div className="field col-12">
                    <label>Originator</label>
                    <Dropdown
                        value={selectedExisting}
                        options={[{ label: '+ New Originator', value: null }, ...existingOptions]}
                        onChange={(e) => handleSelectExisting(e.value)}
                        placeholder="+ New Originator"
                    />
                </div>
                {!selectedExisting && (
                    <div className="field col-6">
                        <label>New Originator slug</label>
                        <InputText value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="e.g. finbee" />
                    </div>
                )}
                <div className="field col-6">
                    <label>
                        Display name (Originator dropdown and PDF title) <PdfSpot spot="name" />
                    </label>
                    <InputText value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Finbee" />
                </div>
            </div>

            <Message
                severity="info"
                className="w-full justify-content-start mb-3"
                text="An Originator has 1 or more borrower types (e.g. Consumer, SME). Most Originators have just 1 - fill in its config below. Click '+ Add Borrower Type' only if this Originator's loans are genuinely split by type, in the same or different collections."
            />

            <div className="p-fluid grid mb-3 pr-form-section">
                    <div className="col-12">
                        <h5 className="pr-form-section-title">1 · Originator</h5>
                        <div className="text-color-secondary text-sm mb-2">Shown in the page header and the Originator Profile on page 1. The numbers show where each value appears in the PDF.</div>
                    </div>
                    <div className="col-12 mb-2">
                        <PdfLayoutGuide />
                    </div>
                    <div className="field col-3">
                        <label>
                            Currency <PdfSpot spot="currency" />
                        </label>
                        <InputText value={topPdf.currency} onChange={(e) => setTopPdf({ ...topPdf, currency: e.target.value })} />
                    </div>
                    <div className="field col-9">
                        <label>
                            Logo <PdfSpot spot="logo" />
                        </label>
                        <div className="flex align-items-center gap-3">
                            {logoPreviewUrl && (
                                // Same white backing plate as the dashboard's
                                // own logo render (below) - an admin uploading
                                // a logo in dark mode should see it exactly as
                                // it'll actually appear elsewhere, not
                                // invisible against a dark preview background.
                                <div style={{ backgroundColor: '#ffffff', borderRadius: '4px', padding: '2px', border: '1px solid var(--app-border-strong)', display: 'inline-flex' }}>
                                    <img src={logoPreviewUrl} alt="Originator logo" style={{ height: '2.5rem', maxWidth: '8rem', objectFit: 'contain' }} />
                                </div>
                            )}
                            {selectedExisting ? (
                                <label className="p-button p-button-outlined p-button-sm" style={{ cursor: logoUploading ? 'wait' : 'pointer' }}>
                                    {logoUploading ? 'Uploading...' : (logoPreviewUrl ? 'Replace logo' : 'Upload logo')}
                                    <input type="file" accept="image/png,image/jpeg" onChange={handleLogoUpload} disabled={logoUploading} style={{ display: 'none' }} />
                                </label>
                            ) : (
                                <small className="text-color-secondary">Save this Originator first, then you can upload its logo.</small>
                            )}
                        </div>
                    </div>
                    <div className="field col-4">
                        <label>
                            Country <PdfSpot spot="country" />
                        </label>
                        <InputText value={topPdf.report_country} onChange={(e) => setTopPdf({ ...topPdf, report_country: e.target.value })} />
                    </div>
                    <div className="field col-4">
                        <label>
                            Credit insurance <PdfSpot spot="creditInsurance" />
                        </label>
                        <InputText value={topPdf.report_credit_insurance} onChange={(e) => setTopPdf({ ...topPdf, report_credit_insurance: e.target.value })} />
                    </div>
                    <div className="field col-4">
                        <label>
                            Operating model <PdfSpot spot="operatingModel" />
                        </label>
                        <InputText value={topPdf.report_operating_model} onChange={(e) => setTopPdf({ ...topPdf, report_operating_model: e.target.value })} />
                    </div>
                    <div className="field col-12">
                        <label>
                            Description <PdfSpot spot="description" />
                        </label>
                        <InputTextarea
                            value={topPdf.report_description}
                            onChange={(e) => setTopPdf({ ...topPdf, report_description: e.target.value })}
                            rows={3}
                            placeholder="2-3 sentences about the Originator, shown under the profile tiles"
                        />
                    </div>
            </div>

            <h5 className="pr-form-section-title mb-2">2 · Borrower types</h5>
            <Accordion multiple activeIndex={[0]} className="pr-segment-accordion">
                {segments.map((segment, i) => (
                    <AccordionTab
                        key={i}
                        headerTemplate={(options) => (
                            <div className="flex align-items-center justify-content-between w-full pr-2">
                                <button type="button" className={options.className} onClick={options.onClick} style={{ flex: 1, textAlign: 'left', background: 'none', border: 'none' }}>
                                    <span className={options.iconClassName} />
                                    <span className="ml-2 font-semibold">{segment.label || segment.name || `Borrower type ${i + 1}`}</span>
                                </button>
                                {isMulti && (
                                    <Button
                                        icon="pi pi-trash"
                                        className="p-button-text p-button-danger p-button-sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeSegment(i);
                                        }}
                                    />
                                )}
                            </div>
                        )}
                    >
                        {/* Exactly 1 borrower type: its internal name is locked
                            to the platform slug (see handleSave) - same
                            invariant as before this form always showed the
                            name field, just now visible/explained instead of
                            hidden. Add a 2nd type to unlock free naming. */}
                        <SegmentFieldSet
                            segment={segment}
                            onChange={(updated) => updateSegment(i, updated)}
                            showNameFields
                            lockedName={isMulti ? null : finalSlugPreview(selectedExisting, slug)}
                            hideCalcConfig={isSpecialCalc}
                            defaults={{
                                // Same fallback as upsertPlatformDoc: {borrower type name}_mapped.
                                collection: `${isMulti ? segment.name || '{type}' : finalSlugPreview(selectedExisting, slug)}_mapped`,
                            }}
                        />
                    </AccordionTab>
                ))}
            </Accordion>

            <Button label="+ Add Borrower Type" icon="pi pi-plus" className="p-button-text mb-2 mt-2" onClick={addSegment} />

            <Accordion className="pr-advanced-accordion mt-2">
                <AccordionTab header="Advanced: data source">
                    <div className="p-fluid grid mb-3 pr-form-section">
                        <div className="field col-6 mb-0">
                            <label className="block mb-2">Calculation type</label>
                            <SelectButton
                                value={isSpecialCalc}
                                options={[
                                    { label: 'General (report_calc_service)', value: false },
                                    { label: 'Special (existing collection)', value: true },
                                ]}
                                onChange={(e) => e.value !== null && setIsSpecialCalc(e.value)}
                            />
                        </div>
                        {isSpecialCalc && (
                            <div className="field col-6 mb-0">
                                <label>Source collection *</label>
                                <InputText value={sourceCollection} onChange={(e) => setSourceCollection(e.target.value)} placeholder="e.g. lend_performance_report_2" />
                                <small className="text-color-secondary">
                                    An existing, already-populated ArangoDB collection this Originator's reports read from directly - no calc step runs, so "Calculation config" is hidden below for each borrower type.
                                </small>
                            </div>
                        )}
                    </div>

                    <Message
                        severity="info"
                        className="w-full justify-content-start mb-3"
                        text={
                            isMulti
                                ? `Each borrower type reads mapped/{collection override, or ${finalSlugPreview(selectedExisting, slug)}_{type}_mapped by default} - seed it first via fetch_real_data.py if needed. The dashboard and downloadable PDF always show the Originator's Overview recomputed across all types together.`
                                : `Data source: mapped/${finalSlugPreview(selectedExisting, slug)}_mapped - seed it first via fetch_real_data.py if you haven't already.`
                        }
                    />
                </AccordionTab>
            </Accordion>

        </Dialog>
    );
}

function finalSlugPreview(selectedExisting, slug) {
    return selectedExisting || slug || '{platform}';
}

// Same palette pdf_generation_service's chart_exporter uses for this
// platform's actual PDF (pdf_generation/_chart_config.py's CHART_COLORS/
// PLATFORM_CONFIG color_primary) - kept in sync manually with that file, the
// same cross-repo convention its own header comment documents versus
// report_calc_service's dashboard/config.py. Every real platform currently
// shares these same values, so this isn't per-platform theming; it's making
// the dashboard's charts and that platform's PDF charts look like the same
// report instead of two differently-colored ones. CHART_COLORS/cyclePalette/
// BASE_OPTIONS themselves live in chart_theme.js - shared with
// LendReportSections.js, which used to keep its own duplicate copy.
// Brand Guidelines' Teal - this dashboard's own shared/generic accent color
// (KPI cards, headline charts), distinct from a given platform's own
// color_primary in PLATFORM_CONFIG which stays untouched (see chart_theme.js).
const COLOR_PRIMARY = '#077186';

// Mirrors sector.py's _sector_colors: a continuous dark-to-light gradient
// (largest slice darkest) sampled for exactly n slices, instead of indexing
// into a fixed-length palette that runs out and repeats.
function sequentialColors(n, fromHex, toHex) {
    if (n <= 0) return [];
    if (n === 1) return [fromHex];
    const from = [1, 3, 5].map((i) => parseInt(fromHex.slice(i, i + 2), 16));
    const to = [1, 3, 5].map((i) => parseInt(toHex.slice(i, i + 2), 16));
    return Array.from({ length: n }, (_, i) => {
        const t = i / (n - 1);
        return `#${from.map((c, idx) => Math.round(c + (to[idx] - c) * t).toString(16).padStart(2, '0')).join('')}`;
    });
}

// Stable (module-scope, not recreated per render) palette-picker functions
// for PieChart's `paletteFn` prop, one per PDF chart this dashboard mirrors:
// sector.py's dark-to-light gradient, purpose.py's/interest_distribution.py's
// flat categorical list, credit_score.py's sequential_blue.
const PALETTE_FN_CATEGORICAL = (n) => cyclePalette(CHART_COLORS.categorical, n);
const PALETTE_FN_SEQUENTIAL_BLUE = (n) => cyclePalette(CHART_COLORS.sequential_blue, n);
// categorical[1] (navy) rather than [0] (teal) - keeps the "largest slice
// darkest" gradient anchored on navy, the darker of the two brand colors,
// regardless of teal now leading the array as the brand's primary color.
const PALETTE_FN_SECTOR = (n) => sequentialColors(n, CHART_COLORS.categorical[1], '#c9dbef');

const pctFormatter = new Intl.NumberFormat('en-IE', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 });
const eurFormatter = new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const eurCompactFormatter = new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', notation: 'compact' });

const toIsoDate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const yearOf = (dateStr) => new Date(dateStr).getFullYear();

// The real (Lambda/ECS) backend's calc trigger is fire-and-forget - POST /
// returns 202 immediately and the actual run takes minutes, so completion is
// polled for via /generations/latest rather than blocking on the request
// itself (which is what the "local" backend still does, synchronously - see
// handleGenerateNew). Either way, report_calc_service/pdf_generation_service
// write real-time progress to the same status doc as they run, so that's
// polled on its own, faster interval for the whole duration of the initiating
// POST too - otherwise "local" mode would show nothing at all until its
// (typically few-second, but occasionally much longer) blocking call returns.
const GENERATION_POLL_INTERVAL_MS = 10000;
// Minimum wait for a new version (~20 minutes). Past that, polling goes on
// for as long as the run's own status doc stays live (report_calc_service
// refreshes it every 2 minutes while it runs - see isActivelyProcessing), so
// a long run (e.g. Savy, 40+ minutes) is never given up on while it's still
// going; a calc deploy that writes no status still stops at this cap.
const GENERATION_POLL_MAX_ATTEMPTS = 120;
const STATUS_POLL_INTERVAL_MS = 1000;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// "fetching_data" -> "Fetching data", "generating_report" -> "Generating report".
const STAGE_LABELS = {
    // Written by exaport itself at trigger time (writeQueuedStatus, backend),
    // until the calc/PDF service starts and overwrites it (the ECS calc task
    // does once it's running; resolveQueuedCalcStatus, backend, covers a calc
    // deploy that doesn't) - labelled as generating, not waiting.
    queued: 'Generating report',
    queued_pdf: 'Waiting for the job to start',
    fetching_data: 'Fetching loanbook data',
    fetching_report_data: 'Fetching report data',
    generating_report: 'Generating report',
    // Combined (multi-type) runs: each borrower type in turn, then the Overview.
    generating_members: 'Generating each borrower type',
    computing_overview: 'Computing the Overview',
    rendering_pdf: 'Rendering PDF',
    running_quality_checks: 'Running data-quality checks',
    uploading: 'Uploading results',
};
const stageLabel = (stage) => STAGE_LABELS[stage] || (stage ? stage.replace(/_/g, ' ') : 'Processing');

// The report-date table's Status tag only distinguishes which SERVICE is
// running (report_calc_service vs pdf_generation_service) - the granular
// stage (see STAGE_LABELS) is still available as a hover tooltip, not the
// main label. "rendering_pdf" is pdf_generation_service's only stage;
// everything else is report_calc_service's own pipeline.
const isPdfStage = (stage) => stage === 'rendering_pdf' || stage === 'queued_pdf';
const isQueuedStage = (stage) => stage === 'queued' || stage === 'queued_pdf';
const serviceLabel = (stage) => (isPdfStage(stage) ? 'PDF generating' : 'Report generating');

// Progress banner for one in-flight status doc (calc or PDF render) - the
// same shared banner Compartment Report shows for its running jobs. A status
// with no progress yet (just triggered, the Lambda/ECS task still starting)
// shows the indeterminate "waiting to start" bar.
function GenerationProgress({ status, reportDate, style }) {
    if (!status) return null;
    const isPdf = isPdfStage(status.stage) || status.kind === 'pdf';
    const queued = isQueuedStage(status.stage) || (status.progress_pct == null && !status.stage);
    return (
        <JobProgress
            title={`${isPdf ? 'Generating PDF' : 'Generating report'} - ${reportDate}`}
            stage={status.stage === 'queued' ? null : queued ? 'waiting for the job to start' : stageLabel(status.stage)}
            pct={status.progress_pct}
            queued={queued}
            style={style}
        />
    );
}

const relativeTimeFormatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
// generation_id is new_generation_id()'s basic-format UTC timestamp
// ("20260917T104359Z") - not directly Date-parseable, so split it into
// extended ISO ("2026-09-17T10:43:59Z") first. Used to show "generated 3
// minutes ago" without the backend needing to add a redundant timestamp
// field - the generation_id already *is* one.
const parseGenerationTimestamp = (generationId) => {
    const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(generationId || '');
    if (!m) return null;
    const [, y, mo, d, h, mi, s] = m;
    const date = new Date(`${y}-${mo}-${d}T${h}:${mi}:${s}Z`);
    return Number.isNaN(date.getTime()) ? null : date;
};
const relativeTimeFromNow = (date) => {
    const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
    const divisions = [
        [60, 'second'],
        [60, 'minute'],
        [24, 'hour'],
        [30, 'day'],
        [12, 'month'],
        [Number.POSITIVE_INFINITY, 'year'],
    ];
    let value = diffSeconds;
    for (const [amount, unit] of divisions) {
        if (Math.abs(value) < amount) return relativeTimeFormatter.format(Math.round(value), unit);
        value /= amount;
    }
    return relativeTimeFormatter.format(Math.round(value), 'year');
};
const generationTimestampLabel = (generationId) => {
    const date = parseGenerationTimestamp(generationId);
    if (!date) return null;
    return `Generated ${relativeTimeFromNow(date)} (${date.toLocaleString()})`;
};

// A backend error normally comes back as {error: "..."} JSON, but a request
// that got cut off by a reverse proxy in front of the API (nginx's own
// default timeouts, e.g.) instead gets THAT proxy's raw HTML error page as
// the body - showing that verbatim in a toast is worse than useless. Handles
// both a `responseType: 'blob'` request (err.response.data is a Blob) and a
// plain JSON request (axios leaves a non-JSON body as a string on
// err.response.data) the same way.
const describeRequestError = async (err) => {
    let bodyText = null;
    if (err.response?.data instanceof Blob) {
        bodyText = await err.response.data.text();
    } else if (typeof err.response?.data === 'string') {
        bodyText = err.response.data;
    }
    if (bodyText != null) {
        try {
            const parsed = JSON.parse(bodyText);
            if (parsed?.error) return parsed.error;
        } catch {
            // Not JSON - fall through to the gateway-timeout check below.
        }
        if (/<html/i.test(bodyText) && err.response.status === 504) {
            return 'The server took too long to respond (gateway timeout) - the report may still be processing in the background. Try again in a bit.';
        }
    }
    return errorMessage(err);
};

// A "processing" status is only trustworthy while it's recent. If the
// writer (report_calc_service/pdf_generation_service) crashes, times out, or
// hits an early-return path that skips its own except-block "failed" write
// (a real, confirmed case: pdf_generation_service's Lend PDF path returns a
// 404 for "no report found" *before* its try/except, so that specific
// failure never got marked "failed" - the status doc was just left at
// "processing"/10% forever), every later view of that exact platform/
// report_date would otherwise show a false "still generating" indicator
// indefinitely. Rather than chasing down every possible early-return path
// across two separate repos, this is the general, self-healing fix: treat
// "processing" as stale once its own `updated_at` is old enough that no real
// single run should still be sitting on one stage, and stop trusting it -
// exactly the same as if no status doc existed at all. 10 minutes is
// comfortably longer than any real checkpoint-to-checkpoint gap (even PDF's
// LaTeX render step), so a genuinely still-running job is never mistaken for
// a stale one.
const PROCESSING_STALE_MS = 10 * 60 * 1000;
const isActivelyProcessing = (status) => {
    if (status?.status !== 'processing') return false;
    if (!status.updated_at) return true;
    return Date.now() - new Date(status.updated_at).getTime() < PROCESSING_STALE_MS;
};

// Every card gets a fixed TOTAL height (title + chart) - not `h-full`
// stretched to match a row sibling. PrimeFlex's .grid stretches every card in
// a flex row to its tallest sibling's natural height (e.g. a pie chart next
// to one with a long legend list), and a hardcoded height on only the inner
// chart div left visible empty space below shorter charts once a taller
// sibling dragged the whole row up. A fixed height on the card itself makes
// every card in every row exactly the same height regardless of neighbors.
// Laid out by the parent's .pr-chart-grid (CSS grid, 2 columns from md up) -
// `wide` spans both columns, for a section with a single chart.
function ChartCard({ title, children, height = 280, wide = false }) {
    return (
        <div className={`pr-card pr-chart-card flex flex-column${wide ? ' is-wide' : ''}`} style={{ height: height + 56 }}>
            <h5 className="pr-card-title flex-shrink-0">{title}</h5>
            {/* minHeight: 0 overrides flexbox's default of never shrinking a
                flex-grow child below its content size - without it, a chart
                with a long legend would force this box (and so the whole
                card) taller instead of Chart.js fitting the legend within it. */}
            <div className="flex-grow-1" style={{ minHeight: 0 }}>{children}</div>
        </div>
    );
}

// Laid out by the parent's .pr-kpi-grid. `icon`/`tone` are presentation only
// (tone="danger"/"warning" tints a tile whose count needs attention).
function KpiTile({ label, value, icon, tone }) {
    return (
        <div className={`pr-kpi${tone ? ` is-${tone}` : ''}`}>
            {icon && (
                <span className="pr-kpi-icon" aria-hidden="true">
                    <i className={`pi ${icon}`} />
                </span>
            )}
            <div className="pr-kpi-body">
                <div className="pr-kpi-label">{label}</div>
                <div className="pr-kpi-value">{value}</div>
            </div>
        </div>
    );
}

// Header row for one block inside a report tab (Loan Exclusions, Data
// Drift, ...): title + description on the left, optional actions right.
function ReviewBlockHeader({ title, description, actions }) {
    return (
        <div className="pr-block-header">
            <div className="min-w-0">
                <h5 className="pr-subheading">{title}</h5>
                {description && <p className="pr-subheading-text">{description}</p>}
            </div>
            {actions}
        </div>
    );
}

// Every chart component below memoizes `data`/`options` on the actual
// underlying data, not just object identity. PrimeReact's <Chart> only
// skips destroying+recreating its Chart.js instance when data/options are
// REFERENCE-equal across renders - building `{...}` literals fresh on every
// render (as these used to) meant ANY unrelated re-render anywhere in this
// page destroyed and recreated every chart, and a recreation could catch its
// container mid-layout before the grid/tab had settled to its final size,
// leaving the canvas visibly short. useMemo keeps the same object reference
// across renders that don't actually change this chart's inputs.

function OriginationChart({ origination }) {
    const data = useMemo(
        () => ({
            labels: origination.map((row) => yearOf(row.loan_origination_date)),
            datasets: [{ label: 'Origination Volume', backgroundColor: COLOR_PRIMARY, data: origination.map((row) => row.loan_original_amount) }],
        }),
        [origination]
    );
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => eurFormatter.format(ctx.raw) } } },
            scales: { y: { ticks: { callback: (v) => eurCompactFormatter.format(v) } } },
        }),
        []
    );
    return <Chart key={chartRemountKey(data)} type="bar" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

function TicketSizeChart({ ticketSize }) {
    const data = useMemo(
        () => ({
            labels: ticketSize.map((row) => yearOf(row.loan_origination_date)),
            datasets: [{ label: 'Average Ticket Size', backgroundColor: COLOR_PRIMARY, data: ticketSize.map((row) => row.loan_original_amount) }],
        }),
        [ticketSize]
    );
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => eurFormatter.format(ctx.raw) } } },
            scales: { y: { ticks: { callback: (v) => eurCompactFormatter.format(v) } } },
        }),
        []
    );
    return <Chart key={chartRemountKey(data)} type="bar" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

function LineOverTimeChart({ rows, label, color, asPercent }) {
    const data = useMemo(
        () => ({
            labels: rows.map((row) => row.loan_origination_date),
            datasets: [{ label, borderColor: color, backgroundColor: color, data: rows.map((row) => (asPercent ? row.value * 100 : row.value)), fill: false, tension: 0.2, pointRadius: 0 }],
        }),
        [rows, label, color, asPercent]
    );
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => (asPercent ? `${ctx.raw.toFixed(1)}%` : ctx.raw.toFixed(1)) } } },
            scales: { y: { ticks: { callback: (v) => (asPercent ? `${v}%` : v) } } },
        }),
        [asPercent]
    );
    return <Chart key={chartRemountKey(data)} type="line" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

// report_generation.db_upload.build_report_document's sector_records comes
// from grouping by whichever column PLATFORM_CONFIG[platform]["sector_column"]
// names for that platform (every platform today sets it to
// "sector_NACE_1_description", lowercased on upload to "sector_nace_1_
// description" - not the "sector_nace_1" this dashboard used to hardcode,
// which made every sector pie slice's label read as undefined). Deriving the
// key from the row itself (whichever field isn't principal_outstanding)
// matches what pdf_generation's sector.py already does server-side, and
// stays correct even if a platform's sector_column config ever changes.
function sectorLabelKey(rows) {
    const row = rows?.[0];
    if (!row) return null;
    return Object.keys(row).find((k) => k !== 'principal_outstanding') ?? null;
}

// valueFormatter defaults to currency since every existing caller charts a
// principal_outstanding breakdown - a plain count breakdown (e.g. loans
// excluded by reason) passes its own formatter instead of showing "€45" for
// a count of 45.
function PieChart({ rows, labelKey, valueKey, paletteFn = PALETTE_FN_CATEGORICAL, valueFormatter = (v) => eurFormatter.format(v) }) {
    const nonZero = useMemo(() => rows.filter((row) => row[valueKey] > 0), [rows, valueKey]);
    const data = useMemo(
        () => ({
            // formatLegendText applied here, not via Chart.js's legend
            // generateLabels default - confirmed the DoughnutController/
            // PieController register their OWN generateLabels override
            // (Chart.overrides.pie/doughnut) which takes precedence over
            // ChartJS.defaults for exactly this chart type, silently
            // bypassing a global-default-level text transform. Formatting
            // the label strings before they ever reach Chart.js sidesteps
            // that resolution order entirely - works regardless of chart
            // type or Chart.js's own per-type override behavior.
            labels: nonZero.map((row) => formatLegendText(String(row[labelKey]))),
            datasets: [{ data: nonZero.map((row) => row[valueKey]), backgroundColor: paletteFn(nonZero.length) }],
        }),
        [nonZero, labelKey, valueKey, paletteFn]
    );
    const options = useMemo(
        () => ({ ...BASE_OPTIONS, plugins: { legend: { position: 'right', labels: { boxWidth: 12 } }, tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${valueFormatter(ctx.raw)}` } } } }),
        [valueFormatter]
    );
    if (!nonZero.length) return <EmptyState icon="pi-chart-pie" title="No data" compact />;
    // See chart_theme.js's chartRemountKey - confirmed bug: switching between
    // two reports with structurally unrelated label sets (e.g. one platform's
    // sector_nace_1_description as long ALL-CAPS NACE text vs. another's as
    // short Title Case names) left stale slices/legend entries from the
    // PREVIOUS chart behind.
    return <Chart key={chartRemountKey(data)} type="pie" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

function InterestDistributionChart({ rows }) {
    const data = useMemo(() => {
        const years = [...new Set(rows.map((row) => yearOf(row.loan_origination_date)))].sort();
        const buckets = [...new Set(rows.map((row) => row.bucket))];
        const bucketColors = cyclePalette(CHART_COLORS.categorical, buckets.length);
        // Same O(1)-lookup reasoning as VintageCurveChart - years.length x
        // buckets.length grows with the loanbook's real size and date range.
        const byYearBucket = new Map(rows.map((row) => [`${yearOf(row.loan_origination_date)}|${row.bucket}`, row.value]));
        const datasets = buckets.map((bucket, i) => ({
            label: formatLegendText(bucket),
            backgroundColor: bucketColors[i],
            data: years.map((year) => (byYearBucket.get(`${year}|${bucket}`) ?? 0) * 100),
        }));
        return { labels: years, datasets };
    }, [rows]);
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } }, tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toFixed(0)}%` } } },
            scales: { x: { stacked: true }, y: { stacked: true, max: 100, ticks: { callback: (v) => `${v}%` } } },
        }),
        []
    );
    return <Chart key={chartRemountKey(data)} type="bar" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

function VintageCurveChart({ rows, label }) {
    const data = useMemo(() => {
        const vintages = [...new Set(rows.map((row) => row.vintage_date))].sort();
        const vintageColors = cyclePalette(CHART_COLORS.vintage_line, vintages.length);
        // Sample every 30th day - a daily curve over years of loans is
        // thousands of points per vintage otherwise, with no visible benefit.
        const sampledRows = rows.filter((row) => row.day_index % 30 === 0);
        const dayIndexes = [...new Set(sampledRows.map((row) => row.day_index))].sort((a, b) => a - b);
        // A larger real loanbook (e.g. savy) has far more (vintage, day_index)
        // pairs than a synthetic one - looking each one up with rows.find()
        // inside a vintages x days loop is effectively O(n^2) and visibly
        // lags. A Map keyed by "vintage|day" makes every lookup O(1) instead.
        const byVintageDay = new Map(sampledRows.map((row) => [`${row.vintage_date}|${row.day_index}`, row.value]));
        const datasets = vintages.map((vintage, i) => ({
            label: vintage.slice(0, 4),
            borderColor: vintageColors[i],
            backgroundColor: vintageColors[i],
            // yearly_loss_vintage/yearly_recovery_vintage's own `value` is a
            // 0-1 fraction (report_calc_service), not already a 0-100
            // percent - scaled here so the y-axis/tooltip can show real
            // percentages instead of e.g. "0.17" reading as 0.17%.
            data: dayIndexes.map((day) => {
                const v = byVintageDay.get(`${vintage}|${day}`);
                return v == null ? null : v * 100;
            }),
            fill: false,
            pointRadius: 0,
            spanGaps: true,
        }));
        return { labels: dayIndexes, datasets };
    }, [rows]);
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } }, tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${(ctx.raw ?? 0).toFixed(2)}%` } } },
            scales: {
                x: { title: { display: true, text: 'Days since origination' } },
                y: { title: { display: true, text: label }, ticks: { callback: (v) => `${v}%` } },
            },
        }),
        [label]
    );
    return <Chart key={chartRemountKey(data)} type="line" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

function RepaymentStatusChart({ rows }) {
    const data = useMemo(() => {
        const sorted = [...rows].sort((a, b) => a.year_month.localeCompare(b.year_month));
        return {
            labels: sorted.map((row) => row.year_month),
            datasets: [
                { label: 'Paid', backgroundColor: CHART_COLORS.repayment_status.principal_paid_cashflows, data: sorted.map((row) => row.principal_paid_cashflows) },
                { label: 'Outstanding', backgroundColor: CHART_COLORS.repayment_status.principal_outstanding, data: sorted.map((row) => row.principal_outstanding) },
                { label: 'Defaulted', backgroundColor: CHART_COLORS.repayment_status.defaulted, data: sorted.map((row) => row.defaulted) },
            ],
        };
    }, [rows]);
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
            scales: { x: { stacked: true }, y: { stacked: true } },
        }),
        []
    );
    return <Chart key={chartRemountKey(data)} type="bar" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

const SEVERITY_TAG = { high: 'danger', medium: 'warning', low: 'info' };
// Icons for the per-severity KPI tiles in the Needs Review tab (presentation only).
const SEVERITY_ICON = { high: 'pi-exclamation-circle', medium: 'pi-exclamation-triangle', low: 'pi-info-circle' };

// Groups a flat anomaly list (one row per loan) into one row per distinct
// `keyFn(anomaly)` - e.g. one row per `check` type, or per drifted field -
// so a human reviewing can see "what's wrong and how often" at a glance
// instead of scrolling a per-loan table that can run into the hundreds of
// rows. `blocking` is true for the group if ANY occurrence in it is
// blocking (a check's blocking-ness is really constant per check, but this
// stays correct even if that ever weren't the case).
function summarizeAnomalies(anomalies, keyFn) {
    const groups = new Map();
    anomalies.forEach((a) => {
        const key = keyFn(a) ?? 'unknown';
        if (!groups.has(key)) {
            groups.set(key, { key, severity: a.severity, blocking: false, count: 0, loanIds: new Set(), sampleMessage: a.message });
        }
        const g = groups.get(key);
        g.count += 1;
        if (a.loan_id) g.loanIds.add(a.loan_id);
        if (a.blocking) g.blocking = true;
    });
    return Array.from(groups.values())
        .map((g) => ({ ...g, loanCount: g.loanIds.size }))
        .sort((a, b) => Number(b.blocking) - Number(a.blocking) || b.count - a.count);
}

// report_generation.lambda_function's preprocess_loanbook/apply_data_rules
// (report_calc_service) - one {reason, count} entry per filter step that
// actually dropped a loan (cancelled, missing data, a configured data_rules
// exclude, etc.), in the order those filters ran. Persisted onto the report
// doc by build_report_document as initial_loan_count/exclusion_summary.
function ExclusionSummarySection({ initialLoanCount, totalLoans, exclusionSummary, onDownload }) {
    const [downloadingExcel, setDownloadingExcel] = useState(false);
    const rows = exclusionSummary || [];
    if (!initialLoanCount && rows.length === 0) return null;
    const excludedTotal = rows.reduce((sum, row) => sum + (row.count || 0), 0);
    const sorted = [...rows].sort((a, b) => b.count - a.count);
    return (
        <section className="pr-block">
            <ReviewBlockHeader
                title="Loan Exclusions"
                description="Loans considered for this report, how many were excluded and why, and how many remain."
                actions={excludedTotal > 0 && onDownload && (
                    <Button
                        label="Download Excel"
                        icon="pi pi-file-excel"
                        className="p-button-outlined p-button-sm"
                        loading={downloadingExcel}
                        onClick={async () => {
                            setDownloadingExcel(true);
                            try {
                                await onDownload();
                            } finally {
                                setDownloadingExcel(false);
                            }
                        }}
                    />
                )}
            />
            <div className="pr-kpi-grid">
                <KpiTile label="Loans Considered" value={initialLoanCount} icon="pi-database" />
                <KpiTile label="Excluded" value={excludedTotal} icon="pi-filter" />
                <KpiTile label="In This Report" value={totalLoans} icon="pi-check-circle" />
            </div>
            {sorted.length > 0 && (
                <div className="pr-split">
                    <ChartCard title="Loans Excluded by Reason">
                        <PieChart rows={sorted} labelKey="reason" valueKey="count" valueFormatter={(v) => `${v} loans`} />
                    </ChartCard>
                    <div className="pr-table-frame">
                        <DataTable value={sorted} size="small" emptyMessage="No loans excluded">
                            <Column field="reason" header="Reason" />
                            <Column field="count" header="Loans Excluded" align="right" style={{ width: '10rem' }} />
                        </DataTable>
                    </div>
                </div>
            )}
        </section>
    );
}

// data_quality_checks.run_all_checks()'s check name for cross-generation
// field drift (check_historical_value_drift) - split into its own "Data
// Drift" section below rather than mixed into the static rule-check table,
// since it answers a different question ("what changed since last time" vs.
// "is this generation internally consistent") and carries structured
// before/after detail (anomaly.details.{field,previous_value,new_value})
// worth its own columns rather than just the generic message string.
const DRIFT_CHECK = 'historical_value_drift';

// data_quality_checks.run_all_checks() (report_calc_service) - needs_review is
// true whenever at least one anomaly here has blocking: true. Non-blocking
// ones (e.g. yield_outlier/yield_divergence at medium severity) are
// informational and don't stop the report from being used.
function ReviewTable({ anomalies, needsReview, initialLoanCount, totalLoans, exclusionSummary, onDownloadExcludedLoans }) {
    const [globalFilter, setGlobalFilter] = useState('');
    const [driftFilter, setDriftFilter] = useState('');
    const driftAnomalies = anomalies.filter((a) => a.check === DRIFT_CHECK);
    const qualityAnomalies = anomalies.filter((a) => a.check !== DRIFT_CHECK);
    // Only set on a combined/group platform's Overview (see group_report.py's
    // overview_anomalies) - each member's own anomaly, tagged with which
    // member it came from so they're distinguishable once merged together.
    // Absent entirely for a single-platform (non-group) report, so the
    // column is hidden rather than showing an always-empty one.
    const hasMemberTag = anomalies.some((a) => a.member);
    const bySeverity = qualityAnomalies.reduce((acc, a) => {
        acc[a.severity] = (acc[a.severity] || 0) + 1;
        return acc;
    }, {});
    const blockingCount = qualityAnomalies.filter((a) => a.blocking).length;
    const driftSummary = summarizeAnomalies(driftAnomalies, (a) => a.details?.field);
    const qualitySummary = summarizeAnomalies(qualityAnomalies, (a) => a.check);

    return (
        <div>
            <ExclusionSummarySection
                initialLoanCount={initialLoanCount}
                totalLoans={totalLoans}
                exclusionSummary={exclusionSummary}
                onDownload={onDownloadExcludedLoans}
            />
            {driftAnomalies.length > 0 && (
                <section className="pr-block">
                    <ReviewBlockHeader
                        title="Data Drift"
                        description="Fields that should be immutable once a loan is reported on changed value since this loan's prior generation - a silent upstream correction/overwrite, not a disclosed one."
                    />
                    <div className="pr-kpi-grid">
                        <KpiTile label="Loans with Drift" value={new Set(driftAnomalies.map((a) => a.loan_id)).size} icon="pi-history" tone="warning" />
                        <KpiTile label="Field Changes" value={driftAnomalies.length} icon="pi-pencil" />
                    </div>
                    <div className="pr-table-frame mb-3">
                        <DataTable value={driftSummary} size="small" emptyMessage="No drift">
                            <Column field="key" header="Field" sortable />
                            <Column field="loanCount" header="Loans Affected" sortable align="right" style={{ width: '10rem' }} />
                            <Column field="count" header="Changes" sortable align="right" style={{ width: '10rem' }} />
                        </DataTable>
                    </div>
                    <Accordion className="pr-detail-accordion">
                        <AccordionTab header={`Loan-level detail (${driftAnomalies.length} rows)`}>
                            <div className="flex justify-content-end mb-3">
                                <span className="p-input-icon-left pr-search">
                                    <i className="pi pi-search" />
                                    <InputText value={driftFilter} onChange={(e) => setDriftFilter(e.target.value)} placeholder="Search loan id, field..." />
                                </span>
                            </div>
                            <DataTable
                                value={driftAnomalies}
                                size="small"
                                stripedRows
                                paginator
                                rows={10}
                                rowsPerPageOptions={[10, 25, 50]}
                                sortField="loan_id"
                                globalFilter={driftFilter}
                                globalFilterFields={['loan_id', 'member', 'details.field']}
                                emptyMessage="No drift matches your search"
                            >
                                {hasMemberTag && <Column field="member" header="Type" sortable style={{ width: '10rem' }} />}
                                <Column field="loan_id" header="Loan ID" sortable />
                                <Column field="details.field" header="Field" sortable />
                                <Column field="details.previous_value" header="Previous Value" body={(row) => String(row.details?.previous_value ?? '')} />
                                <Column field="details.new_value" header="New Value" body={(row) => String(row.details?.new_value ?? '')} />
                            </DataTable>
                        </AccordionTab>
                    </Accordion>
                </section>
            )}
            {qualityAnomalies.length > 0 && (
                <section className="pr-block">
                    <ReviewBlockHeader
                        title="Data Quality Anomalies"
                        description={needsReview
                            ? 'This generation has at least one blocking data-quality issue - review before relying on it.'
                            : 'No blocking issues on this generation; anything below is informational only.'}
                    />
                    <div className="pr-kpi-grid">
                        <KpiTile label="Total Anomalies" value={qualityAnomalies.length} icon="pi-search" />
                        <KpiTile label="Blocking" value={blockingCount} icon="pi-ban" tone={blockingCount > 0 ? 'danger' : undefined} />
                        {Object.entries(bySeverity).map(([sev, count]) => (
                            <KpiTile key={sev} label={`${sev[0].toUpperCase()}${sev.slice(1)} Severity`} value={count} icon={SEVERITY_ICON[sev] || 'pi-info-circle'} />
                        ))}
                    </div>
                    <div className="pr-table-frame mb-3">
                        <DataTable value={qualitySummary} size="small" emptyMessage="No anomalies">
                            <Column field="key" header="Check" sortable bodyClassName="pr-cell-code" />
                            <Column field="severity" header="Severity" sortable body={(row) => <Tag severity={SEVERITY_TAG[row.severity] || 'info'} value={row.severity} />} style={{ width: '8rem' }} />
                            <Column field="blocking" header="Blocking" sortable body={(row) => (row.blocking ? <Tag severity="danger" value="Blocking" /> : <Tag severity="success" value="Informational" />)} style={{ width: '9rem' }} />
                            <Column field="loanCount" header="Loans Affected" sortable align="right" style={{ width: '9rem' }} />
                            <Column field="count" header="Occurrences" sortable align="right" style={{ width: '9rem' }} />
                            <Column field="sampleMessage" header="Example Message" bodyClassName="pr-cell-muted" />
                        </DataTable>
                    </div>
                    <Accordion className="pr-detail-accordion">
                        <AccordionTab header={`Loan-level detail (${qualityAnomalies.length} rows)`}>
                            <div className="flex justify-content-end mb-3">
                                <span className="p-input-icon-left pr-search">
                                    <i className="pi pi-search" />
                                    <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search loan id, check, message..." />
                                </span>
                            </div>
                            <DataTable
                                value={qualityAnomalies}
                                size="small"
                                stripedRows
                                paginator
                                rows={10}
                                rowsPerPageOptions={[10, 25, 50]}
                                sortField="severity"
                                sortOrder={-1}
                                globalFilter={globalFilter}
                                globalFilterFields={['loan_id', 'check', 'message', 'severity', 'member']}
                                emptyMessage="No anomalies match your search"
                            >
                                <Column field="severity" header="Severity" sortable body={(row) => <Tag severity={SEVERITY_TAG[row.severity] || 'info'} value={row.severity} />} style={{ width: '10rem' }} />
                                <Column field="blocking" header="Blocking" sortable body={(row) => (row.blocking ? <Tag severity="danger" value="Blocking" /> : <Tag severity="success" value="Informational" />)} style={{ width: '10rem' }} />
                                {hasMemberTag && <Column field="member" header="Type" sortable style={{ width: '10rem' }} />}
                                <Column field="check" header="Check" sortable />
                                <Column field="loan_id" header="Loan ID" sortable />
                                <Column field="message" header="Message" />
                            </DataTable>
                        </AccordionTab>
                    </Accordion>
                </section>
            )}
        </div>
    );
}

// Small local Combined/type switcher, one per report section below - same
// role as LendReportSections.js's <SegmentToggle>: purely client-side, picks
// which of the already-fetched `docsByView` entries this ONE section reads,
// with no refetch. Hidden entirely for a single-type platform (`options` is
// empty then, see `typeOptions` in the main component).
function TypeToggle({ value, onChange, options }) {
    if (!options.length) return null;
    return (
        <div className="pr-type-toggle">
            <span className="pr-type-toggle-label">Borrower type</span>
            <SelectButton value={value} options={options} onChange={(e) => (e.value !== undefined ? onChange(e.value) : null)} />
        </div>
    );
}

function NoDataForType() {
    return <EmptyState icon="pi-filter-slash" message="Not available for this borrower type on this version." compact />;
}

function OverviewTabContent({ docsByView, typeOptions }) {
    const [view, setView] = useState(null);
    const doc = docsByView[view];
    return (
        <div>
            <TypeToggle value={view} onChange={setView} options={typeOptions} />
            {!doc ? (
                <NoDataForType />
            ) : (
                <div className="pr-chart-grid">
                    <ChartCard title="Origination Volume by Year"><OriginationChart origination={doc.origination} /></ChartCard>
                    <ChartCard title="Average Gross Yield Over Time"><LineOverTimeChart rows={doc.interest} label="Gross Yield" color={COLOR_PRIMARY} asPercent /></ChartCard>
                    <ChartCard title="Average Term Over Time (months)"><LineOverTimeChart rows={doc.term} label="Term" color={COLOR_PRIMARY} /></ChartCard>
                    <ChartCard title="Average Ticket Size by Year"><TicketSizeChart ticketSize={doc.ticket_size} /></ChartCard>
                </div>
            )}
        </div>
    );
}

function CompositionTabContent({ docsByView, typeOptions }) {
    const [view, setView] = useState(null);
    const doc = docsByView[view];
    return (
        <div>
            <TypeToggle value={view} onChange={setView} options={typeOptions} />
            {!doc ? (
                <NoDataForType />
            ) : (
                <div className="pr-chart-grid">
                    {doc.sectors?.length > 0 && (
                        <ChartCard title="Outstanding Principal by Sector">
                            <PieChart rows={doc.sectors} labelKey={sectorLabelKey(doc.sectors)} valueKey="principal_outstanding" paletteFn={PALETTE_FN_SECTOR} />
                        </ChartCard>
                    )}
                    {doc.purpose?.length > 0 && (
                        <ChartCard title="Outstanding Principal by Purpose"><PieChart rows={doc.purpose} labelKey="loan_purpose" valueKey="principal_outstanding" /></ChartCard>
                    )}
                    {doc.credit_score?.length > 0 && (
                        <ChartCard title="Outstanding Principal by Credit Score">
                            <PieChart rows={doc.credit_score} labelKey="source_platform_credit_rating" valueKey="principal_outstanding" paletteFn={PALETTE_FN_SEQUENTIAL_BLUE} />
                        </ChartCard>
                    )}
                    {doc.interest_distribution?.length > 0 && (
                        <ChartCard title="Interest Rate Distribution by Vintage"><InterestDistributionChart rows={doc.interest_distribution} /></ChartCard>
                    )}
                </div>
            )}
        </div>
    );
}

function VintageTabContent({ docsByView, typeOptions }) {
    const [view, setView] = useState(null);
    const doc = docsByView[view];
    return (
        <div>
            <TypeToggle value={view} onChange={setView} options={typeOptions} />
            {!doc ? (
                <NoDataForType />
            ) : (
                <>
                    <h5 className="pr-subheading mb-2">Performance by vintage</h5>
                    <div className="pr-table-frame mb-4">
                    <DataTable value={doc.vintage_performance} size="small" rowClassName={(row) => (row.vintage_date === 'Totals' ? 'pr-total-row' : '')}>
                        {/* report_calc_service appends a "Totals" summary row
                            (vintage_date: "Totals", a literal string, not a
                            real date - see build_report_document) with every
                            other column already correctly aggregated -
                            yearOf() tries to parse it as a date and returns
                            NaN, showing "NaN" instead of the row's own label. */}
                        <Column field="vintage_date" header="Year" body={(row) => (row.vintage_date === 'Totals' ? 'Total' : yearOf(row.vintage_date))} />
                        <Column field="num_of_loans" header="# Loans" align="right" />
                        <Column field="originated_principal" header="Originated Principal" align="right" body={(row) => eurFormatter.format(row.originated_principal)} />
                        <Column field="principal_repaid" header="Principal Repaid" align="right" body={(row) => eurFormatter.format(row.principal_repaid)} />
                        <Column field="performing_balance" header="Performing Balance" align="right" body={(row) => eurFormatter.format(row.performing_balance)} />
                        <Column field="defaulted_principal" header="Defaulted Principal" align="right" body={(row) => eurFormatter.format(row.defaulted_principal)} />
                        <Column field="net_loss_pct" header="Net Loss %" align="right" body={(row) => pctFormatter.format(row.net_loss_pct)} />
                    </DataTable>
                    </div>
                    <div className="pr-chart-grid">
                        {doc.yearly_loss_vintage?.length > 0 && (
                            <ChartCard title="Loss by Vintage" height={340}><VintageCurveChart rows={doc.yearly_loss_vintage} label="Cumulative loss" /></ChartCard>
                        )}
                        {doc.yearly_recovery_vintage?.length > 0 && (
                            <ChartCard title="Recovery by Vintage" height={340}><VintageCurveChart rows={doc.yearly_recovery_vintage} label="Cumulative recovery" /></ChartCard>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

function RepaymentTabContent({ docsByView, typeOptions }) {
    const [view, setView] = useState(null);
    const doc = docsByView[view];
    return (
        <div>
            <TypeToggle value={view} onChange={setView} options={typeOptions} />
            {!doc?.repayment_status_by_vintage?.length ? (
                <NoDataForType />
            ) : (
                <div className="pr-chart-grid">
                    <ChartCard title="Repayment Status by Month" height={360} wide><RepaymentStatusChart rows={doc.repayment_status_by_vintage} /></ChartCard>
                </div>
            )}
        </div>
    );
}

function ReviewTabContent({ docsByView, typeOptions, onDownloadExcludedLoans }) {
    const [view, setView] = useState(null);
    const doc = docsByView[view];
    return (
        <div>
            <TypeToggle value={view} onChange={setView} options={typeOptions} />
            {!doc ? (
                <NoDataForType />
            ) : (
                <ReviewTable
                    anomalies={doc.anomalies || []}
                    needsReview={doc.needs_review}
                    initialLoanCount={doc.initial_loan_count}
                    totalLoans={doc.total_loans}
                    exclusionSummary={doc.exclusion_summary}
                    onDownloadExcludedLoans={() => onDownloadExcludedLoans(view)}
                />
            )}
        </div>
    );
}

// Builds the tab-descriptor array (key/header/content) from every borrower
// type's already-fetched doc at once - `docsByView[null]` is combined/the
// only type, `docsByView[typeName]` each other type. Each tab's own local
// Combined/type toggle (see the *TabContent components above) then picks
// which of these already-fetched docs IT reads, entirely client-side - same
// pattern LendReportSections.js already uses, just per-tab here instead of
// one big pre-merged document (report_calc_service writes 3 separate
// documents for a multi-type platform - combined/each type - not one
// document with every type's fields already merged in, unlike Lend's).
// A tab's visibility is "does ANY view have this data", so composition/
// repayment/review still show up even if only one borrower type has that
// content - the tab's own toggle handles "no data for this selection".
function buildTabsForDocsByView(docsByView, typeOptions, onDownloadExcludedLoans) {
    const views = Object.values(docsByView).filter(Boolean);
    if (!views.length) return [];
    const anyView = (pick) => views.some(pick);
    return [
        {
            key: 'overview',
            header: 'Overview',
            content: <OverviewTabContent docsByView={docsByView} typeOptions={typeOptions} />,
        },
        {
            key: 'composition',
            header: 'Loanbook Composition',
            content: <CompositionTabContent docsByView={docsByView} typeOptions={typeOptions} />,
        },
        {
            key: 'vintage',
            header: 'Vintage Performance',
            content: <VintageTabContent docsByView={docsByView} typeOptions={typeOptions} />,
        },
        anyView((d) => d.repayment_status_by_vintage?.length > 0) && {
            key: 'repayment',
            header: 'Repayment Status',
            content: <RepaymentTabContent docsByView={docsByView} typeOptions={typeOptions} />,
        },
        anyView((d) => d.anomalies?.length > 0 || d.exclusion_summary?.length > 0) && {
            key: 'review',
            header: 'Needs Review',
            content: <ReviewTabContent docsByView={docsByView} typeOptions={typeOptions} onDownloadExcludedLoans={onDownloadExcludedLoans} />,
        },
    ].filter(Boolean);
}

// Tab header icons (presentation only), keyed by buildTabsForDocsByView's tab keys.
const TAB_ICONS = { overview: 'pi-chart-bar', composition: 'pi-chart-pie', vintage: 'pi-history', repayment: 'pi-wallet', review: 'pi-flag' };

// Full tab set for one platform's report, all sections sharing one
// already-fetched `docsByView` (combined + every borrower type) - own
// self-contained active-tab state, same lazy-render pattern as before.
function ReportDocTabs({ docsByView, typeOptions, onDownloadExcludedLoans }) {
    const [activeTab, setActiveTab] = useState(0);
    const tabs = buildTabsForDocsByView(docsByView, typeOptions, onDownloadExcludedLoans);
    if (!tabs.length) return <LoadingState label="Loading report" />;
    return (
        <TabView scrollable className="pr-report-tabs" activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
            {tabs.map((tab, i) => (
                <TabPanel key={tab.key} header={tab.header} leftIcon={TAB_ICONS[tab.key] ? `pi ${TAB_ICONS[tab.key]}` : undefined}>
                    {activeTab === i && tab.content}
                </TabPanel>
            ))}
        </TabView>
    );
}

function ReportDocKpis({ doc }) {
    return (
        <div className="pr-kpi-grid pr-kpi-grid-4">
            <KpiTile label="Total Loans" value={doc.total_loans} icon="pi-list" />
            <KpiTile label="Origination Volume (since start)" value={eurFormatter.format(doc.portfolio.origination_volume_since_start)} icon="pi-euro" />
            <KpiTile label="Average Gross Yield" value={pctFormatter.format(doc.portfolio.average_gross_yield)} icon="pi-percentage" />
            <KpiTile label="Portfolio IRR" value={pctFormatter.format(doc.portfolio.portfolio_irr)} icon="pi-chart-line" />
            <KpiTile label="Credit Loss" value={pctFormatter.format(doc.portfolio.credit_loss)} icon="pi-arrow-down-right" />
            <KpiTile label="Recovery Rate" value={pctFormatter.format(doc.portfolio.recovery_rate)} icon="pi-replay" />
            <KpiTile label="Capital in Workout" value={pctFormatter.format(doc.portfolio.capital_in_workout)} icon="pi-briefcase" />
            <KpiTile label="Average Term (months)" value={doc.portfolio.average_term.toFixed(1)} icon="pi-calendar" />
        </div>
    );
}

// The KPI row's own local Combined/type toggle, mirroring the tab sections -
// this row lives above the tabs, so it needs its own wrapper rather than
// being one more entry in buildTabsForDocsByView.
function ReportDocKpisSection({ docsByView, typeOptions }) {
    const [view, setView] = useState(null);
    const doc = docsByView[view];
    return (
        <section className="pr-kpi-section">
            <div className="pr-kpi-section-header">
                <div>
                    <h4 className="pr-section-title">Portfolio summary</h4>
                    <div className="pr-section-subtitle">Headline KPIs for this report version</div>
                </div>
                <TypeToggle value={view} onChange={setView} options={typeOptions} />
            </div>
            {doc ? <ReportDocKpis doc={doc} /> : <NoDataForType />}
        </section>
    );
}

const PlatformReport = ({ setLoading: setAppLoading = () => {} }) => {
    // App.js's own `loading` is a plain boolean, fine everywhere else in the
    // app since each of those pages only ever has one loading operation
    // in flight at a time. This page can have several running concurrently
    // (loadGenerations alongside LendDocTabs' own loanbook/rating-tables
    // fetches, which start once reportDoc is set) - a raw boolean setter
    // shared between them means whichever finishes first turns the overlay
    // off while the others are still running (confirmed via screenshot: the
    // overlay vanished, leaving only LendDocTabs' own small local spinner
    // for the rest of the wait). Reference-counted locally instead - the
    // real setAppLoading(false) only fires once every concurrent caller
    // here has finished, not on the first one to finish.
    const loadingCountRef = useRef(0);
    const beginLoading = useCallback(() => {
        loadingCountRef.current += 1;
        if (loadingCountRef.current === 1) setAppLoading(true);
    }, [setAppLoading]);
    const endLoading = useCallback(() => {
        loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
        if (loadingCountRef.current === 0) setAppLoading(false);
    }, [setAppLoading]);
    // The logged-in account's own platform (Cognito custom:Platform, e.g.
    // "Savy") vs. this page's own "platform" state (which report to show) -
    // renamed on destructure so the two never collide. In production, a
    // platform account only ever sees its own data (a Savy login never sees
    // Anodu/Finbee/etc.), so anything that would expose or let them switch
    // to another platform's data is gated on isExaloanMember below. A
    // missing/not-yet-loaded accountPlatform defaults to full access rather
    // than locking the UI - this is a UI-only convenience restriction (the
    // API itself isn't platform-scoped), not the actual access boundary.
    const { platform: accountPlatform } = useAuth();
    const isExaloanMember = !accountPlatform || accountPlatform.toLowerCase() === 'exaloan';
    const { theme } = useTheme();
    // Global Chart.js defaults, not this component's own chart `options`
    // objects - see chart_theme.js's applyChartJsTheme docstring for why.
    // Runs on mount and every theme toggle so already-open charts still
    // pick up the change next time they update (e.g. switching report date/
    // tab/version) even without a full page reload. applyChartColorsTheme
    // remaps the handful of CHART_COLORS entries (navy chief among them)
    // that are otherwise nearly invisible against the dark card background -
    // see that function's docstring.
    useEffect(() => {
        applyChartJsTheme(theme);
        applyChartColorsTheme(theme);
    }, [theme]);

    // Remembered across a page refresh (like Compartment Report's open
    // reporting), so a run that's still going is visible again straight
    // away rather than only after re-picking the platform. Validated against
    // this account's own platform list once it loads (below).
    const [platform, setPlatform] = useLocalStorage('platform_report_platform', null);
    const [uploadedLogoUrl, setUploadedLogoUrl] = useState(null);
    const [reportDate, setReportDate] = useState(null);
    const [generations, setGenerations] = useState([]);
    const [selectedGenerationId, setSelectedGenerationId] = useState(null);
    const [reportDoc, setReportDoc] = useState(null);
    const [error, setError] = useState(null);
    // `busy`: quick version-switch/lookup fetch (small inline spinner only).
    // `downloading`: the actual long-running PDF work - a Toast on
    // completion, not just a spinner, so it's clear when it's actually done.
    // Generation itself has no equivalent page-level flag any more - each
    // report_date's own row in the overview table (live_status, kept fresh
    // by loadOverview polling) is the single source of truth for whether
    // IT is generating, checked per-date wherever that matters (see
    // isDateGenerating below) rather than one flag for "anything, anywhere".
    const [busy, setBusy] = useState(false);
    const [downloading, setDownloading] = useState(false);
    // Latest status of the PDF render this tab is waiting on (doDownloadPdf's
    // poll loop), for the result view's progress banner - null when idle.
    const [pdfJob, setPdfJob] = useState(null);
    const [platformConfigs, setPlatformConfigs] = useState([]);
    const [onboardDialogVisible, setOnboardDialogVisible] = useState(false);
    // Every borrower type's own doc for the currently-loaded generation
    // (multi-type platforms only), keyed by type name - fetched once
    // alongside the combined doc (see the effect below `reportDoc` is set
    // from) so each report section's own local Combined/type toggle can
    // switch instantly with no refetch, same pattern LendReportSections.js
    // already uses. `null` for a type means its fetch failed (e.g. a stale
    // generation from before "Generate New Version" always regenerated
    // every type together) - sections show "not available" for that case
    // rather than crashing.
    const [typeDocs, setTypeDocs] = useState({});
    // 'form' = platform/date/version pickers visible, no result shown yet
    // (or the user clicked "Back" to change them); 'result' = pickers
    // hidden, report shown full-width - see the reportDoc effect below.
    const [viewMode, setViewMode] = useState('form');
    const toast = useRef(null);

    // Whether GET /platform-configs has answered at least once - lets a
    // platform account that isn't onboarded get the "subscribe" message
    // below instead of an empty page, without flashing it before the list
    // has loaded.
    const [platformConfigsLoaded, setPlatformConfigsLoaded] = useState(false);
    const loadPlatformConfigs = useCallback(async () => {
        try {
            const res = await axiosWithAuth.get('/generate-platform-report/platform-configs');
            setPlatformConfigs(res.data.platforms);
            setPlatformConfigsLoaded(true);
        } catch (err) {
            setError(errorMessage(err));
        }
    }, []);

    useEffect(() => {
        loadPlatformConfigs();
    }, [loadPlatformConfigs]);

    // Non-Exaloan accounts never see the platform dropdown at all (below) -
    // this is what actually puts them on their own platform's report
    // instead, matching accountPlatform to a known platform slug
    // case-insensitively (Cognito's custom:Platform is display-cased, e.g.
    // "Savy", platformConfigs' keys are lowercase slugs, e.g. "savy").
    // Drops a remembered platform this account can no longer see (removed,
    // or a different login on the same browser).
    useEffect(() => {
        if (!platformConfigsLoaded || !platform) return;
        if (!platformConfigs.some((p) => p.platform === platform)) setPlatform(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [platformConfigsLoaded, platformConfigs, platform]);

    // Also overrides a remembered platform that isn't this account's own
    // (e.g. one picked as staff before switching to View As).
    useEffect(() => {
        if (isExaloanMember || !platformConfigs.length) return;
        const mine = platformConfigs.find((p) => p.platform.toLowerCase() === (accountPlatform || '').toLowerCase());
        if (mine && platform !== mine.platform) setPlatform(mine.platform);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExaloanMember, platform, platformConfigs, accountPlatform]);


    const platformOptions = platformConfigs.map((p) => ({ label: p.display_name || p.platform, value: p.platform }));
    const currentPlatformDoc = platformConfigs.find((p) => p.platform === platform) || null;
    // Lend is a read-only, fixed-shape report over a separate collection
    // (the "source" field, now settable for any platform via the onboarding
    // dialog's "Special calculation" option - see backend's resolveTarget)
    // - not a {platform}_platform_report doc report_calc_service produced,
    // so it renders its own tabs/KPIs (LendReportSections.js) instead of
    // ReportDocKpis/ReportDocTabs, and has no calc step to trigger. Kept
    // keyed to Lend's exact collection name rather than "any source
    // platform is generic" - LendReportSections only knows Lend's own field
    // shape (performance_report/vintage/index), so a hypothetical future
    // second special-calculation platform would need its own bespoke
    // rendering component too, not a reuse of this one; the backend side of
    // "no calc step" (resolveTarget/POST '/') is already generic today.
    const isLendPlatform = currentPlatformDoc?.source === 'lend_performance_report_2';
    // Mozzeno is the same kind of read-only source platform, over
    // mozzeno_performance_report, with its own bespoke tabs
    // (MozzenoReportSections.js). isSourcePlatform gates everything the two
    // share: no calc step, no versions, no per-segment report fetches.
    const isMozzenoPlatform = currentPlatformDoc?.source === 'mozzeno_performance_report';
    const isSourcePlatform = isLendPlatform || isMozzenoPlatform;
    const segments = currentPlatformDoc?.segments || [];
    const isMulti = segments.length > 1;
    // Options shared by every report section's own local Combined/type
    // toggle (see ReportDocKpisSection/ReportDocTabs below) - "Combined"
    // matches Lend's own combined-option label ("All Loans" via
    // SEGMENT_LABELS.total in LendReportSections.js) rather than the
    // platform's own name, for consistency across both report styles.
    const typeOptions = isMulti
        ? [{ label: 'Combined', value: null }, ...segments.map((s) => ({ label: s.label || s.name, value: s.name }))]
        : [];
    // Page header's logo is always the platform's own top-level branding
    // (falling back to the first type's, for an older platform_config that
    // predates the "Platform branding" onboarding section) - each report
    // section below has its own local type toggle now, so there's no single
    // "currently selected type" at the page level to derive this from.
    const activeLogoS3Key = currentPlatformDoc?.pdf_config?.logo_s3_key || segments[0]?.pdf_config?.logo_s3_key;
    // Only the real uploaded logo (same S3 object pdf_generation_service now
    // fetches - see aws_helper.js's getPresignedPlatformReportAssetUrl) - the
    // old static Frontend/public/platform-logos/ fallback was a *third* place
    // a logo had to be kept in sync (alongside the Python service's own
    // bundled assets/logos/, already removed) and is gone now too. A platform
    // with no upload yet just shows a text placeholder (below) instead.
    const platformLogoUrl = uploadedLogoUrl;

    useEffect(() => {
        if (!platform || !activeLogoS3Key) {
            setUploadedLogoUrl(null);
            return;
        }
        let cancelled = false;
        axiosWithAuth.get(`/generate-platform-report/platform-configs/${platform}/logo-url`)
            .then((res) => { if (!cancelled) setUploadedLogoUrl(res.data.logo_url); })
            .catch(() => { if (!cancelled) setUploadedLogoUrl(null); });
        return () => { cancelled = true; };
    }, [platform, activeLogoS3Key]);

    // Once a report is loaded/generated, get the input fields out of the way
    // and show the result full-width - "Back" (below) brings them back to
    // change platform/date/version. Re-fires (and so re-shows the result)
    // any time a *different* report doc arrives - a fresh generation, or a
    // different version/segment picked from the (now-hidden) form - without
    // fighting a manual "Back" click when nothing has actually changed yet.
    useEffect(() => {
        if (reportDoc) setViewMode('result');
    }, [reportDoc]);

    // The report-date overview table (below), for every platform including
    // Lend - every report_date this platform has ever been generated for (or
    // is currently generating), replacing the old flow of first guessing/
    // picking a report_date before seeing anything at all.
    const [overviewRows, setOverviewRows] = useState([]);
    const [overviewLoading, setOverviewLoading] = useState(false);
    // Whether THIS platform's overview has completed at least one load yet -
    // a ref, not a read of `overviewRows.length` inside loadOverview itself,
    // because that read would be captured by this useCallback's closure (only
    // recreated when `platform` changes) and go stale: if overviewRows was
    // still [] at the render where a platform was first picked, every later
    // call through that SAME closure - including every background poll -
    // would keep seeing "still the initial load", flashing the global
    // overlay on every poll for as long as that platform stays selected
    // (confirmed: exactly the repeated loading-UI-every-few-seconds behavior
    // while something's generating for a freshly-selected platform). A ref
    // mutates in place, so every call - poll or not - reads its true current
    // value regardless of which render's closure is running.
    const overviewLoadedRef = useRef(false);
    useEffect(() => {
        overviewLoadedRef.current = false;
    }, [platform]);
    const loadOverview = useCallback(async () => {
        if (!platform) {
            setOverviewRows([]);
            return;
        }
        setOverviewLoading(true);
        // Same global overlay+spinner as everywhere else now (see
        // loadGenerations' own comment) - but only for a genuine first load
        // (table still empty), not the background polling this same
        // function also serves (the "processing" row refresh right after
        // triggering a generation, and the interval poll while one's active -
        // both call this repeatedly once the first load has already landed).
        const isInitialLoad = !overviewLoadedRef.current;
        if (isInitialLoad) beginLoading();
        try {
            const res = await axiosWithAuth.get('/generate-platform-report/overview', { params: { platform } });
            const rows = res.data.dates || [];
            // Background polls mostly return the same rows - keeping the
            // current array then means the table doesn't re-render at all.
            setOverviewRows((current) => (JSON.stringify(current) === JSON.stringify(rows) ? current : rows));
            overviewLoadedRef.current = true;
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setOverviewLoading(false);
            if (isInitialLoad) endLoading();
        }
    }, [platform, beginLoading, endLoading]);

    useEffect(() => {
        loadOverview();
    }, [loadOverview]);

    // A platform account's own Generate New Version/Regenerate allowance
    // this month (GET /generation-quota - capped server-side, see
    // PLATFORM_GENERATION_MONTHLY_LIMIT in generate_platform_report.js).
    // `limited: false` for Exaloan staff; null until loaded or on error,
    // which leaves the buttons enabled (the backend still enforces the cap).
    const [generationQuota, setGenerationQuota] = useState(null);
    const loadGenerationQuota = useCallback(async () => {
        if (!platform) {
            setGenerationQuota(null);
            return;
        }
        try {
            const res = await axiosWithAuth.get('/generate-platform-report/generation-quota', { params: { platform } });
            setGenerationQuota(res.data || null);
        } catch {
            setGenerationQuota(null);
        }
    }, [platform]);

    useEffect(() => {
        loadGenerationQuota();
    }, [loadGenerationQuota]);

    const generationLimitReached = Boolean(generationQuota?.limited && generationQuota.remaining <= 0);

    // A brand-new platform (just onboarded, or any platform with genuinely
    // zero prior generations) has nothing in overviewRows and no "View" row
    // to seed reportDate from - "Generate New Version" stayed silently
    // disabled (disabled={... || !reportDate}) with the "Report date to
    // generate" Calendar empty, and no visible reason why, easily read as
    // "nothing happened" after onboarding + trying to generate the first
    // report. Defaults it to the most recent calendar month-end (the
    // convention every report_date in this app already follows) as soon as
    // the overview table has definitively finished loading and is empty -
    // still fully editable, just no longer starts blank. Lend has no
    // "generate" affordance at all (see the isSourcePlatform-gated block
    // below), so there's nothing for this to unblock there.
    useEffect(() => {
        if (isSourcePlatform || overviewLoading || overviewRows.length || reportDate) return;
        const lastDayOfPreviousMonth = new Date();
        lastDayOfPreviousMonth.setDate(0);
        setReportDate(lastDayOfPreviousMonth);
    }, [isSourcePlatform, overviewLoading, overviewRows, reportDate]);

    // Keeps the table's live status current while at least one of its rows
    // is mid-generation - e.g. someone else (or another tab) triggered it, so
    // this tab never ran handleGenerateNew's own poll loop for it. Runs on
    // both the overview screen and an open report date, so the progress
    // banners (GenerationProgress) stay live wherever they're shown.
    useEffect(() => {
        if (!overviewRows.some((r) => isActivelyProcessing(r.live_status))) return;
        const interval = setInterval(loadOverview, STATUS_POLL_INTERVAL_MS * 3);
        return () => clearInterval(interval);
    }, [overviewRows, loadOverview]);

    const loadDoc = useCallback(async (generationId) => {
        setError(null);
        try {
            const res = await axiosWithAuth.get('/generate-platform-report', {
                params: { platform, report_date: toIsoDate(reportDate), generation_id: generationId || undefined },
            });
            setReportDoc(res.data);
            setSelectedGenerationId(res.data.generation_id);
        } catch (err) {
            setError(errorMessage(err));
            setReportDoc(null);
        }
    }, [platform, reportDate]);

    // `silent`: after a background generation finishes - the new version is
    // swapped in place, without the full-screen overlay (no "page refresh").
    const loadGenerations = useCallback(async ({ silent = false } = {}) => {
        if (!platform || !reportDate) return;
        setBusy(true);
        // Same global overlay+spinner every other page (e.g. FileUpload.js)
        // already uses for this exact kind of short fetch - a custom inline
        // banner/spinner here (tried, reverted) ended up positioned wherever
        // this component happened to render it (bottom of a tall page in
        // practice), inconsistent with how loading looks everywhere else in
        // the app. Only for this quick version-switch/lookup fetch, not
        // generating/downloading below - those are long-running background
        // jobs that intentionally shouldn't block the whole screen for
        // minutes (see doDownloadPdf's own comments on why).
        if (!silent) beginLoading();
        setError(null);
        try {
            const res = await axiosWithAuth.get('/generate-platform-report/generations', {
                params: { platform, report_date: toIsoDate(reportDate) },
            });
            setGenerations(res.data.generations);
            if (res.data.generations.length) {
                await loadDoc(res.data.generations[0].generation_id);
            } else {
                setReportDoc(null);
                setSelectedGenerationId(null);
            }
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setBusy(false);
            if (!silent) endLoading();
        }
    }, [platform, reportDate, loadDoc, beginLoading, endLoading]);

    // Every borrower type's own doc, fetched once per (multi-type) combined
    // generation - each report section's local Combined/type toggle then
    // just picks which of these already-fetched docs to read, no refetch.
    // Promise.allSettled (not .all): a type's fetch can legitimately 404 for
    // an old generation from before "Generate New Version" always
    // regenerated every type together (see handleGenerateNew) - that type's
    // sections just show "not available for this version" (docsByView entry
    // stays null) rather than the whole page failing.
    useEffect(() => {
        // Lend has real segments too (private/sme/mortgage) now, purely for
        // the onboarding dialog's "Borrower types" editor to be accurate -
        // but its actual report content (<LendDocTabs>) never reads
        // docsByView/typeOptions at all (it has its own internal
        // SegmentToggle, entirely separate from this generic machinery). A
        // per-segment fetch here would just hit report_calc_service-style
        // endpoints Lend never populates (there's no lend_private_platform_
        // report collection) - wasted requests and backend error noise for
        // zero visible effect, so skip it outright for Lend.
        if (!isMulti || !reportDoc || isSourcePlatform) {
            setTypeDocs({});
            return;
        }
        let cancelled = false;
        (async () => {
            const results = await Promise.allSettled(
                segments.map((s) =>
                    axiosWithAuth.get('/generate-platform-report', {
                        params: { platform, segment: s.name, report_date: toIsoDate(reportDate), generation_id: reportDoc.generation_id },
                    })
                )
            );
            if (cancelled) return;
            const next = {};
            segments.forEach((s, i) => {
                const r = results[i];
                next[s.name] = r.status === 'fulfilled' ? r.value.data : null;
            });
            setTypeDocs(next);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMulti, reportDoc?.generation_id, platform, reportDate, isSourcePlatform]);

    // `docsByView[null]` (combined/only type) or `docsByView[typeName]` -
    // the shape every report section's local toggle reads from.
    const docsByView = { ...typeDocs, [null]: reportDoc };

    useEffect(() => {
        loadGenerations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [platform, reportDate]);

    // Resumes tracking a generation that's still running for this exact
    // platform/report_date from *before* this page load - e.g. the user (or
    // another tab) started one, then refreshed. The table's own row already
    // shows it's still in flight either way (fetchReportDateOverview,
    // backend), but without this, a refresh mid-run means no one's left
    // polling to surface the eventual success/failure Toast, and the row
    // sits there without ever getting a final answer. Only kicks in when the
    // status doc says "processing" right now - a normal, not-yet-started or
    // long-finished platform/report_date does one quick, silent status check
    // and stops.
    useEffect(() => {
        if (!platform || !reportDate) return;
        let cancelled = false;
        const isoDate = toIsoDate(reportDate);
        (async () => {
            let status;
            try {
                const res = await axiosWithAuth.get('/generate-platform-report/generation-status', { params: { platform, report_date: isoDate } });
                status = res.data?.status || null;
            } catch {
                return; // no status doc / endpoint error - nothing to resume
            }
            if (cancelled || !isActivelyProcessing(status)) return;

            // No attempt cap: runs while the status doc stays live (refreshed
            // by the calc every 2 minutes), ends on succeeded/failed/stale.
            while (!cancelled) {
                await sleep(STATUS_POLL_INTERVAL_MS * 3);
                if (cancelled) return;
                try {
                    const res = await axiosWithAuth.get('/generate-platform-report/generation-status', { params: { platform, report_date: isoDate } });
                    status = res.data?.status || null;
                } catch {
                    continue;
                }
                if (cancelled) return;
                if (!isActivelyProcessing(status)) break;
            }
            if (cancelled) return;
            if (status?.status === 'failed') {
                const message = status.error || `${platform} / ${isoDate} failed during generation.`;
                setError(message);
                toast.current?.show({ severity: 'error', summary: 'Generation failed', detail: message, sticky: true });
            } else if (status?.status === 'succeeded') {
                await loadGenerations({ silent: true });
                toast.current?.show({
                    severity: 'success',
                    summary: 'Report generated',
                    detail: `${platform} / ${isoDate} - new version ready.`,
                    life: 5000,
                });
            } else {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Still running',
                    detail: `${platform} / ${isoDate} is taking longer than expected - check back later.`,
                    sticky: true,
                });
            }
            await loadOverview();
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [platform, reportDate]);

    // Always triggers the combined/group calc (no `segment`) - for a
    // multi-type platform this already regenerates every borrower type
    // together under one shared generation_id (report_calc_service's
    // generate_group_report), so every type's data stays in sync with the
    // combined doc. Deliberately removed the old ability to regenerate just
    // one type on its own from this button - that was the only way a type's
    // generation_id could drift out of sync with the combined one.
    // `dateOverride` (a Date object) lets the per-row "Regenerate" action in
    // the report-date overview table below trigger a specific row's date
    // directly, without the classic stale-closure bug a
    // `setReportDate(...); handleGenerateNew()` one-two would have - the
    // latter reads `reportDate` from this render's closure, which still has
    // the OLD value at the point handleGenerateNew actually runs (the
    // setReportDate call it's paired with hasn't re-rendered yet). Defaults
    // to the `reportDate` Calendar field's own value (the "generate a new
    // report date" affordance next to the table, which has no row to pass a
    // date from).
    const handleGenerateNew = async (dateOverride) => {
        const targetDate = dateOverride || reportDate;
        if (!platform || !targetDate) {
            setError('Select an Originator and a report date.');
            return;
        }
        setError(null);
        const isoDate = toIsoDate(targetDate);
        setReportDate(targetDate);
        const targetLabel = `${platform} / ${isoDate}`;

        // Runs for the whole duration of this generate attempt, independent
        // of whether the initiating POST below returns instantly (202, real
        // backend) or blocks until the run is done ("local" backend) - either
        // way report_calc_service/pdf_generation_service write real-time
        // progress to the same status doc, so this is the only thing that
        // needs to know which backend is in play. `latestStatus` (a plain
        // variable, not state) lets the polling loop below react to a
        // "failed" write without a stale closure over React state.
        let latestStatus = null;
        let statusPollActive = true;
        (async () => {
            while (statusPollActive) {
                try {
                    const statusRes = await axiosWithAuth.get('/generate-platform-report/generation-status', { params: { platform, report_date: isoDate } });
                    latestStatus = statusRes.data?.status || null;
                    // Keeps the overview table's own row (renderStatusTag) live
                    // for the whole run, not just the 202/real-backend branch -
                    // the "local" backend's POST below blocks until the run is
                    // done, so without this the table's synthetic in-progress
                    // row (fetchReportDateOverview, backend) never refreshes,
                    // and there's nothing else left to show a run is in
                    // progress. Not awaited: independent of the status GET
                    // above, no ordering needed between the two.
                    loadOverview();
                } catch {
                    // Not fatal - a missing/older calc or PDF deploy just
                    // means no status doc exists yet; keep polling.
                }
                await sleep(STATUS_POLL_INTERVAL_MS);
            }
        })();

        try {
            // Captured before triggering so an async ("started") response can
            // be polled for a NEW generation_id to appear, rather than
            // guessing what it'll eventually be.
            let previousGenerationId = null;
            try {
                const latestRes = await axiosWithAuth.get('/generate-platform-report/generations/latest', { params: { platform } });
                previousGenerationId = latestRes.data?.latest?.generation_id || null;
            } catch {
                // Not fatal - just means we can't tell "new" from "none yet"
                // quite as precisely below; polling still works either way.
            }

            const res = await axiosWithAuth.post('/generate-platform-report', {
                platform,
                report_date: isoDate,
            });

            if (res.status === 202) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Report generation started',
                    detail: `${targetLabel} - this can take several minutes.`,
                    life: 5000,
                });
                // Otherwise the overview table only refreshes in this
                // function's own `finally` (i.e. once the whole run is
                // already done) - a platform's very first-ever generation
                // (empty table, "No reports generated yet") got zero visual
                // feedback there while running. This makes a "processing"
                // row for isoDate show up immediately, which also satisfies
                // the polling effect below the table (it only starts polling
                // once a row's own live_status is already active).
                await loadOverview();
                let found = null;
                let failedStatus = null;
                for (let attempt = 0; attempt < GENERATION_POLL_MAX_ATTEMPTS || isActivelyProcessing(latestStatus); attempt++) {
                    await sleep(GENERATION_POLL_INTERVAL_MS);
                    if (latestStatus?.status === 'failed') {
                        failedStatus = latestStatus;
                        break;
                    }
                    const latestRes = await axiosWithAuth.get('/generate-platform-report/generations/latest', { params: { platform } });
                    const latest = latestRes.data?.latest;
                    if (latest && latest.report_date === isoDate && latest.generation_id !== previousGenerationId) {
                        found = latest;
                        break;
                    }
                }
                if (failedStatus) {
                    const message = failedStatus.error || `${targetLabel} failed during generation.`;
                    setError(message);
                    toast.current?.show({ severity: 'error', summary: 'Generation failed', detail: message, sticky: true });
                    return;
                }
                if (!found) {
                    toast.current?.show({
                        severity: 'warn',
                        summary: 'Still running',
                        detail: `${targetLabel} is taking longer than expected - check back later.`,
                        sticky: true,
                    });
                    return;
                }
                setSelectedGenerationId(found.generation_id);
                await loadGenerations({ silent: true });
            } else {
                // "local" backend - synchronous, result already in res.data.
                setReportDoc(res.data);
                setSelectedGenerationId(res.data.generation_id);
                await loadGenerations({ silent: true });
            }
            toast.current?.show({
                severity: 'success',
                summary: 'Report generated',
                detail: `${targetLabel} - new version ready.`,
                life: 5000,
            });
        } catch (err) {
            // Backend's no-op guard (generate_platform_report.js's POST /) -
            // neither the mapped collection nor the onboarding config has
            // changed since this exact platform/report_date's last generation, so nothing new was
            // triggered. Not an error - surfaced as an info toast, not the
            // page-level error banner.
            if (err.response?.status === 409 && err.response?.data?.error === 'no_new_data') {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Nothing to regenerate',
                    detail: err.response.data.message || `${targetLabel} - no changes to the raw data or the Originator configuration since the last generation.`,
                    life: 7000,
                });
                return;
            }
            if (err.response?.status === 429 && err.response?.data?.error === 'monthly_generation_limit') {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Monthly limit reached',
                    detail: err.response.data.message,
                    life: 10000,
                });
                return;
            }
            const message = await describeRequestError(err);
            setError(message);
            toast.current?.show({ severity: 'error', summary: 'Calc failed', detail: message, sticky: true });
        } finally {
            statusPollActive = false;
            loadOverview();
            loadGenerationQuota();
        }
    };

    // `segment` (a borrower type name, or undefined/null for combined/All) -
    // picked from the "Download PDF" split-button's dropdown for a
    // multi-type platform (single-type platforms only ever call this with
    // no segment, same as before). Each type's own PDF is generated the same
    // way any other individual-mode PDF always has been (buildPdfPayload's
    // existing `mode === 'individual'` branch, backend, unchanged).
    //
    // `variant` only applies to a multi-type platform's combined download
    // (no segment): "overview" (the default - just the Overview section,
    // same layout/disclaimer) or "full" (Overview plus every type's own
    // section). See POST /pdf's resolvePdfVariant.
    //
    // Gated on that segment's own needs_review flag (docsByView[segment] -
    // reportDoc itself for combined/undefined): a blocking data-quality
    // issue shouldn't go out the door in a PDF without the reviewer
    // explicitly acknowledging they've looked at the Needs Review tab first.
    const handleDownloadPdf = (segment, variant) => {
        const doc = docsByView[segment || null];
        if (doc?.needs_review) {
            confirmDialog({
                message: 'This generation has at least one blocking data-quality issue (see the Needs Review tab). Please confirm you have reviewed it before downloading.',
                header: 'Confirm review before download',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'I have reviewed it, download anyway',
                rejectLabel: 'Cancel',
                accept: () => doDownloadPdf(segment, variant),
            });
            return;
        }
        doDownloadPdf(segment, variant);
    };

    const doDownloadPdf = async (segment, variantIn) => {
        setError(null);
        setDownloading(true);

        const variant = isMulti && !segment ? variantIn || 'overview' : undefined;
        // Same (platform, segment, variant) the POST below sends - GET
        // /generation-status resolves the status doc key from these exactly
        // the way POST /pdf does (namespaced member key, group key, or the
        // group's separate Overview-only key), rather than this guessing it.
        const statusParams = { platform, segment: segment || undefined, variant };
        const isoDate = toIsoDate(reportDate);

        const fetchBytes = (generationIdOverride) =>
            axiosWithAuth.post(
                '/generate-platform-report/pdf',
                { platform, segment: segment || undefined, variant, report_date: isoDate, generation_id: generationIdOverride !== undefined ? generationIdOverride : selectedGenerationId },
                { responseType: 'blob', validateStatus: (s) => s === 200 || s === 202 }
            );

        // Overwritten below if the 202/poll path resolves to a different
        // actual version than what was requested (see that branch's own
        // comment) - used both for the follow-up fetch and the downloaded
        // filename, so the two can never disagree with each other.
        let resolvedGenerationId = selectedGenerationId;

        try {
            let res = await fetchBytes();

            // 202 - no cached PDF yet (POST /pdf's own comment): rendering
            // was just fired off server-side and keeps running independently
            // of this request/tab, so this polls the same generation-status
            // doc "Generate New Version" already reads (pdf_generation_service
            // writes pdf_s3_key onto the report doc, THEN marks the status
            // doc "succeeded" - see lambda_handler_group - so by the time
            // this observes "succeeded" the one follow-up fetch below is
            // guaranteed a cache hit) rather than re-POSTing every tick,
            // which would otherwise re-fire a brand new render each time.
            // Refreshing the page here loses nothing but this poll loop -
            // clicking Download again just re-enters this function and
            // either resumes polling (still 202) or gets the cached PDF
            // immediately (200, already finished in the meantime).
            if (res.status === 202) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Preparing PDF',
                    detail: 'This can take a few minutes. Safe to navigate away or refresh - just click Download again once ready.',
                    life: 8000,
                });
                setPdfJob({ status: 'processing', kind: 'pdf', report_date: isoDate });
                let succeeded = false;
                let failure = null;
                // The status doc's own `generation_id` (report_generation/
                // db_upload.py's update_generation_status) is whatever
                // pdf_generation_service actually *resolved* and rendered -
                // not necessarily selectedGenerationId, e.g. this download
                // was requested without pinning a version (generation_id
                // omitted, resolves to "latest" server-side) or the pinned
                // one didn't exist and it fell back. The follow-up fetch
                // below needs this exact value: fetchReportDoc's cache check
                // matches on generation_id, so re-sending the original
                // (possibly different) selectedGenerationId would find no
                // pdf_s3_key on that specific doc and 202 forever - confirmed
                // the hard way ("PDF finished generating but is not
                // available yet", looping).
                for (let attempt = 0; attempt < GENERATION_POLL_MAX_ATTEMPTS && !failure && !succeeded; attempt++) {
                    await sleep(GENERATION_POLL_INTERVAL_MS);
                    try {
                        const statusRes = await axiosWithAuth.get('/generate-platform-report/generation-status', { params: { ...statusParams, report_date: isoDate } });
                        const status = statusRes.data?.status || null;
                        if (status?.status === 'succeeded') {
                            succeeded = true;
                            resolvedGenerationId = status.generation_id;
                        } else if (status?.status === 'failed') {
                            failure = new Error(status.error || 'PDF generation failed');
                        } else if (isActivelyProcessing(status)) {
                            setPdfJob({ ...status, kind: 'pdf', report_date: isoDate });
                        }
                    } catch {
                        // Not fatal - a missing/older-deploy status doc, or a
                        // transient network error against this endpoint
                        // itself, just means "can't tell yet" - keep polling
                        // rather than giving up on the whole download.
                    }
                }
                if (failure) throw failure;
                if (!succeeded) {
                    throw new Error('Still generating after several minutes - try Download again shortly.');
                }
                res = await fetchBytes(resolvedGenerationId);
                if (res.status !== 200) {
                    throw new Error('PDF finished generating but is not available yet - try Download again.');
                }
            }

            const blob = new Blob([res.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            // Includes the actual version just downloaded (resolvedGenerationId -
            // defaults to selectedGenerationId, only differs when the 202/poll
            // path resolved to a different one, see its own comment above) and
            // shared across combined + every member since they're always
            // generated together - see pdf_generation_service's
            // lambda_handler_group "pinned_generation_id" - so downloading two
            // different versions of the same report_date never produces two
            // files with the identical name/content silently overwriting each
            // other locally.
            // "n/a" (not null/undefined) is Lend's own literal placeholder for
            // "no real generation_id" (see backend POST /pdf's own
            // versionSuffixFor) - filtered out here too so a Lend filename
            // doesn't end up "lend-2026-07-31-n_a.pdf".
            const versionSuffix = resolvedGenerationId && resolvedGenerationId !== 'n/a' ? `-${resolvedGenerationId}` : '';
            const filename = segment
                ? `${platform}-${segment}-${isoDate}${versionSuffix}.pdf`
                : `${platform}${variant === 'overview' ? '-overview' : ''}-${isoDate}${versionSuffix}.pdf`;
            link.download = filename;
            link.click();
            URL.revokeObjectURL(link.href);
            toast.current?.show({ severity: 'success', summary: 'PDF ready', detail: `${filename} downloaded.`, life: 5000 });
        } catch (err) {
            const message = await describeRequestError(err);
            setError(message);
            toast.current?.show({ severity: 'error', summary: 'PDF generation failed', detail: message, sticky: true });
        } finally {
            setDownloading(false);
            setPdfJob(null);
        }
    };

    // Passed down to the Needs Review tab's "Download Excel" button (see
    // ExclusionSummarySection) - takes that tab's own local Combined/type
    // selection (this file no longer has a page-level segment concept).
    // Separate loading state from the PDF download's `downloading` so the
    // two buttons don't spin together.
    const handleDownloadExcludedLoans = async (segment) => {
        setError(null);
        try {
            const res = await axiosWithAuth.get('/generate-platform-report/excluded-loans', {
                params: { platform, segment: segment || undefined, report_date: toIsoDate(reportDate), generation_id: selectedGenerationId },
                responseType: 'blob',
            });
            const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            const filename = segment
                ? `${platform}-${segment}-${toIsoDate(reportDate)}-excluded-loans.xlsx`
                : `${platform}-${toIsoDate(reportDate)}-excluded-loans.xlsx`;
            link.download = filename;
            link.click();
            URL.revokeObjectURL(link.href);
            toast.current?.show({ severity: 'success', summary: 'Excel ready', detail: `${filename} downloaded.`, life: 5000 });
        } catch (err) {
            const message = await describeRequestError(err);
            setError(message);
            toast.current?.show({ severity: 'error', summary: 'Excel download failed', detail: message, sticky: true });
        }
    };

    const generationOptions = generations.map((g) => ({
        label: `${new Date(g.ingested_at).toLocaleString()}${g.needs_review ? ' (needs review)' : ''}`,
        value: g.generation_id,
    }));

    // The only thing worth blocking anything for: triggering a second calc
    // run for a report_date that's already mid-generation (report_calc_
    // service/pdf_generation_service are Lambdas writing their own DB rows
    // keyed by generation_id, so two DIFFERENT dates - or platforms -
    // generating at once is completely safe). overviewRows' own live_status
    // per row (kept fresh by loadOverview polling, both here and inside
    // handleGenerateNew) is the single source of truth for that, checked
    // per-date wherever it matters rather than one flag blocking everything
    // everywhere - View, Regenerate-a-different-date, switching platform,
    // downloading a PDF, none of those actually conflict with a run elsewhere.
    const dateLiveStatus = (isoDate) => overviewRows.find((r) => r.report_date === isoDate)?.live_status;
    const isDateGenerating = (isoDate) => isActivelyProcessing(dateLiveStatus(isoDate));

    const showResult = viewMode === 'result' && reportDoc && !busy;

    // Shared by the table's Status column AND the result view's own inline
    // indicator below - the SAME live_status doc for a report_date, so both
    // read it the same way. A "full" PDF download writes to this same doc
    // too (same group key GET /overview looks up for the combined/no-segment
    // case), not just "Generate New Version"/Regenerate - the Overview-only
    // download tracks its own separate status doc instead (see POST /pdf's
    // pdfStatusPlatform), so it doesn't show up here - serviceLabel
    // buckets by the doc's own `stage` (rendering_pdf vs everything else) so
    // this always says which of the two is actually running, rather than
    // assuming it's always a new report version.
    // Same ex-status pills as Compartment Report's tables (ui-kit.css).
    function renderLiveStatusTag(liveStatus) {
        if (!isActivelyProcessing(liveStatus)) return null;
        return (
            <span className="ex-status ex-status-info" title={stageLabel(liveStatus.stage)}>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.75rem' }} />
                {`${serviceLabel(liveStatus.stage)}${liveStatus.progress_pct != null ? ` ${liveStatus.progress_pct}%` : ''}`}
            </span>
        );
    }

    // One row of the report-date overview table (below) - priority order
    // matters: a row can have both a completed prior version AND a currently
    // failed/in-progress regenerate attempt (the status doc is overwritten
    // per run, independent of how many completed versions already exist), so
    // "what's happening right now" always wins over "what the last
    // completed version looked like".
    const renderStatusTag = (row) => {
        const liveTag = renderLiveStatusTag(row.live_status);
        if (liveTag) return liveTag;
        if (row.live_status?.status === 'failed') {
            return <span className="ex-status ex-status-danger" title={row.live_status.error || undefined}>Failed</span>;
        }
        if (!row.versions_count) {
            return <span className="ex-status">No versions</span>;
        }
        return <span className={`ex-status ${row.needs_review ? 'ex-status-warning' : 'ex-status-success'}`}>{row.needs_review ? 'Needs review' : 'OK'}</span>;
    };


    // A platform account whose own platform isn't onboarded to Platform
    // Report yet - the sidebar still links here for every platform account,
    // so explain how to get access rather than showing an empty dashboard.
    const notSubscribed = !isExaloanMember
        && platformConfigsLoaded
        && !platformConfigs.some((p) => p.platform.toLowerCase() === (accountPlatform || '').toLowerCase());
    if (notSubscribed) {
        return (
            <div className="content-container pr-page">
                <PageHeader
                    title="Risk Analytics Dashboard"
                    subtitle="Monthly Originator Reports."
                    icon="pi-chart-bar"
                />
                <EmptyState
                    icon="pi-lock"
                    title="Originator Report isn't enabled for your account yet"
                    message="Originator Report provides a monthly performance report of your loan book - interactive dashboards plus a downloadable PDF. To subscribe to this feature, please reach out to the Exaloan business team."
                />
            </div>
        );
    }

    return (
        <div className="content-container pr-page">
            <Toast ref={toast} />
            <ConfirmDialog />

            <PageHeader
                title="Risk Analytics Dashboard"
                subtitle="Monthly Originator Reports - pick an Originator, then open or generate a report date."
                icon="pi-chart-bar"
                actions={viewMode === 'form' && isExaloanMember && (
                    <Button
                        label="Onboard Originator"
                        icon="pi pi-plus-circle"
                        className="p-button-outlined"
                        onClick={() => setOnboardDialogVisible(true)}
                    />
                )}
            />

            {viewMode === 'form' && (
                <section className="ex-section pr-selection">
                    <div className="pr-selection-top">
                        <div className="pr-field pr-platform-field">
                            <label className="pr-field-label" htmlFor="platform">Originator</label>
                            {isExaloanMember ? (
                                <Dropdown inputId="platform" value={platform} options={platformOptions} onChange={(e) => setPlatform(e.value)} placeholder="Select an Originator" className="w-full" />
                            ) : (
                                // Platform accounts only ever see their own platform -
                                // shown read-only instead of a dropdown.
                                <InputText id="platform" value={currentPlatformDoc?.display_name || platform || ''} disabled className="w-full" />
                            )}
                        </div>
                    </div>

                    {/* Lend has no calc/generation-versioning concept - one doc
                        per report_date, produced by a separate process (see
                        fetchLendGenerations, backend) - so its rows always
                        have versions_count=1/live_status=null (GET /overview's
                        Lend branch, backend) and never show a "Regenerate"
                        button or the "generate a new date" affordance below
                        (there's no calc step to trigger, it would just 400) -
                        otherwise it's the exact same table every other
                        platform gets, just read-only. */}
                    {platform ? (
                        <>
                            {/* The global overlay (setLoading, see loadOverview) already
                                covers the initial fetch - no separate inline spinner
                                needed here for that case, just the empty/populated
                                states below. */}
                            <div className="flex align-items-center gap-2 mb-2">
                                <h4 className="pr-section-title">Report dates</h4>
                                {overviewRows.length > 0 && <span className="pr-count-pill">{overviewRows.length}</span>}
                            </div>
                            {/* One progress banner per report date still running -
                                driven by the polled status docs, so it reappears
                                after a refresh or coming back to this page while
                                a generation is in flight. */}
                            {overviewRows
                                .filter((row) => isActivelyProcessing(row.live_status))
                                .map((row) => (
                                    <GenerationProgress key={row.report_date} status={row.live_status} reportDate={row.report_date} style={{ marginBottom: '0.75rem' }} />
                                ))}
                            {overviewRows.length ? (
                                <div className="pr-table-frame mb-4">
                                    <DataTable value={overviewRows} dataKey="report_date" size="small" paginator rows={5} className="pr-overview-table">
                                        <Column field="report_date" header="Report date" bodyClassName="pr-cell-strong" style={{ width: '9rem' }} />
                                        <Column header="Versions" body={(row) => row.versions_count} align="right" style={{ width: '6.5rem' }} />
                                        <Column header="Status" body={renderStatusTag} style={{ width: '13rem' }} />
                                        <Column
                                            header="Generated"
                                            bodyClassName="pr-cell-muted"
                                            body={(row) => (row.generation_id ? generationTimestampLabel(row.generation_id) : '–')}
                                        />
                                        <Column
                                            header=""
                                            headerClassName="pr-actions-col"
                                            bodyClassName="pr-actions-col"
                                            body={(row) => (
                                                <div className="flex gap-1 justify-content-end">
                                                    {row.versions_count > 0 && (
                                                        <Button
                                                            label="View"
                                                            icon="pi pi-eye"
                                                            className="p-button-text p-button-sm"
                                                            onClick={() => setReportDate(new Date(row.report_date))}
                                                        />
                                                    )}
                                                    {!isSourcePlatform && (
                                                        <Button
                                                            label="Regenerate"
                                                            icon="pi pi-refresh"
                                                            className="p-button-text p-button-secondary p-button-sm"
                                                            onClick={() => handleGenerateNew(new Date(row.report_date))}
                                                            disabled={isActivelyProcessing(row.live_status) || generationLimitReached}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        />
                                    </DataTable>
                                </div>
                            ) : (
                                <div className="pr-table-frame mb-4">
                                    <EmptyState
                                        icon="pi-file"
                                        compact
                                        message={isSourcePlatform
                                            ? `No ${currentPlatformDoc?.display_name || platform} reports available.`
                                            : 'No reports generated yet for this Originator - pick a report date below to generate the first one.'}
                                    />
                                </div>
                            )}

                            {!isSourcePlatform && (
                                <>
                                    <div className="pr-generate-bar">
                                        <div className="pr-generate-text">
                                            <div className="pr-generate-title">Generate a report</div>
                                            <div className="pr-generate-hint">Runs the calculation for the chosen report date as a new version. This can take several minutes.</div>
                                            {generationQuota?.limited && (
                                                <div className="pr-generate-hint">
                                                    {generationQuota.remaining} of {generationQuota.limit} generations left this month (Generate New Version and Regenerate combined).
                                                </div>
                                            )}
                                        </div>
                                        <div className="pr-generate-controls">
                                            <div className="pr-field">
                                                <label className="pr-field-label" htmlFor="newReportDate">Report date to generate</label>
                                                <Calendar inputId="newReportDate" value={reportDate} onChange={(e) => setReportDate(e.value)} dateFormat="yy-mm-dd" showIcon />
                                            </div>
                                            <Button
                                                label="Generate New Version"
                                                icon="pi pi-refresh"
                                                onClick={() => handleGenerateNew()}
                                                disabled={!reportDate || isDateGenerating(toIsoDate(reportDate)) || generationLimitReached}
                                                loading={Boolean(reportDate) && isDateGenerating(toIsoDate(reportDate))}
                                            />
                                        </div>
                                    </div>
                                    {/* No separate progress bar here any more - a newly
                                        triggered generation gets its own synthetic row in
                                        the table above instead (fetchReportDateOverview,
                                        backend; renderStatusTag here), kept live for the
                                        whole run by the loadOverview() poll inside
                                        handleGenerateNew's status-polling loop. One
                                        "what's generating" UI instead of two. */}
                                </>
                            )}
                        </>
                    ) : (
                        <EmptyState
                            icon="pi-building"
                            compact
                            title="Select an Originator"
                            message="Its report dates, versions and generation status will show up here."
                        />
                    )}
                </section>
            )}

            <OnboardPlatformDialog
                visible={onboardDialogVisible}
                onHide={() => setOnboardDialogVisible(false)}
                platformConfigs={platformConfigs}
                toast={toast}
                onSaved={async (savedPlatform) => {
                    await loadPlatformConfigs();
                    setPlatform(savedPlatform);
                }}
            />

            {error && <div className="ex-inline-error mb-3">{error}</div>}

            {showResult && (
                <div className="pr-result">
                    <div className="pr-result-nav">
                        {/* Navigating back doesn't stop or interfere with a running
                            generation: handleGenerateNew's own status poller and the
                            overview table's polling (loadOverview) both run
                            independently of viewMode, so a running generation
                            keeps being tracked (and eventually shows up in the
                            table) whether or not you stay on this screen. */}
                        <Button label="Back" icon="pi pi-arrow-left" className="p-button-text p-button-sm" onClick={() => setViewMode('form')} />
                        {/* Exaloan staff previously had to click "Back" (above) just to
                            reach the platform Dropdown on the form screen before they
                            could look at a different platform - a two-step detour every
                            single time, since "Back" alone doesn't change platform. This
                            switches platform directly from the result screen instead,
                            landing on that platform's own selection/overview screen the
                            same way "Back" + picking it there would (reportDate/reportDoc
                            are reset since they belonged to the platform being left -
                            leaving them would risk loadGenerations picking up a
                            date that happens to also exist for the new platform). Not
                            shown for platform accounts - they only ever have their own
                            one platform (see the isExaloanMember-gated Dropdown on the
                            form screen, same options/reasoning). */}
                        {isExaloanMember && (
                            <div className="pr-switch-platform">
                                <label className="pr-field-label mb-0" htmlFor="switchPlatform">Originator</label>
                                <Dropdown
                                    inputId="switchPlatform"
                                    value={platform}
                                    options={platformOptions}
                                    onChange={(e) => {
                                        setPlatform(e.value);
                                        setReportDate(null);
                                        setReportDoc(null);
                                        setViewMode('form');
                                    }}
                                    placeholder="Switch Originator"
                                    style={{ minWidth: '200px' }}
                                />
                            </div>
                        )}
                    </div>

                    <section className="ex-section pr-report-header">
                        <div className="pr-report-identity">
                            {platformLogoUrl ? (
                                // A platform's uploaded logo is whatever colors that
                                // platform's own brand uses - most were uploaded
                                // against this page's original light background and
                                // go effectively invisible on dark theme (e.g. a navy
                                // wordmark on a near-navy dark card). Fixed to a
                                // white backing plate always, not just in dark mode -
                                // it reads correctly either way and doesn't require
                                // guessing a given logo's own colors to know whether
                                // dark mode specifically needs help.
                                <div className="pr-logo-plate">
                                    <img src={platformLogoUrl} alt={currentPlatformDoc?.display_name || platform} style={{ height: 40, maxWidth: 180, objectFit: 'contain' }} />
                                </div>
                            ) : (
                                // No uploaded logo yet - neutral placeholder tile.
                                <span className="pr-logo-placeholder" title={`${platform}_logo`} aria-hidden="true">
                                    <i className="pi pi-building" />
                                </span>
                            )}
                            <div className="pr-report-heading">
                                <div className="pr-report-eyebrow">Originator Report</div>
                                <div className="flex align-items-center flex-wrap gap-2">
                                    <h3 className="pr-report-title">{currentPlatformDoc?.display_name || reportDoc.platform}</h3>
                                    <span className={`ex-status ${reportDoc.needs_review ? 'ex-status-warning' : 'ex-status-success'}`}>{reportDoc.needs_review ? 'Needs review' : 'OK'}</span>
                                    {renderLiveStatusTag(dateLiveStatus(reportDoc.report_date))}
                                </div>
                                <div className="pr-report-meta">
                                    <span><i className="pi pi-calendar" aria-hidden="true" /> Report date <strong>{reportDoc.report_date}</strong></span>
                                    <span className="pr-report-meta-sep" aria-hidden="true" />
                                    <span className="pr-report-slug">{reportDoc.platform}</span>
                                </div>
                            </div>
                        </div>
                        <div className="pr-report-actions">
                            {/* Source platforms (Lend/Mozzeno) render one fixed PDF
                                regardless of segment/variant, so they get the plain
                                button rather than the Overview/full/per-type menu. */}
                            {isMulti && !isSourcePlatform ? (
                                <SplitButton
                                    label="Download Overview PDF"
                                    icon="pi pi-file-pdf"
                                    onClick={() => handleDownloadPdf(undefined, 'overview')}
                                    model={[
                                        {
                                            label: 'Download full PDF (Overview + all types)',
                                            command: () => handleDownloadPdf(undefined, 'full'),
                                        },
                                        ...segments.map((s) => ({
                                            label: `Download ${s.label || s.name} PDF`,
                                            command: () => handleDownloadPdf(s.name),
                                        })),
                                    ]}
                                    loading={downloading}
                                />
                            ) : (
                                <Button
                                    label="Download PDF"
                                    icon="pi pi-file-pdf"
                                    onClick={() => handleDownloadPdf()}
                                    loading={downloading}
                                />
                            )}
                        </div>
                        {/* Always visible without "Back" - which version is currently
                            shown, how stale it is, and (when this report_date has
                            more than one) a way to switch between them right here,
                            the exact thing that was missing when landing straight
                            on the dashboard after picking a platform/date - the
                            overview table's "View" only ever opens the latest one.
                            Lend has no calc/generation-versioning concept - its
                            doc's generation_id is always null (see
                            fetchLendGenerations) - so there's nothing meaningful to
                            show here for it. */}
                        {reportDoc.generation_id && (
                            <div className="pr-version-bar">
                                {generations.length > 1 ? (
                                    <>
                                        <label className="pr-field-label mb-0" htmlFor="reportVersion"><i className="pi pi-clone" aria-hidden="true" /> Version</label>
                                        <Dropdown
                                            inputId="reportVersion"
                                            value={selectedGenerationId}
                                            options={generationOptions}
                                            onChange={(e) => loadDoc(e.value)}
                                            style={{ minWidth: '280px' }}
                                        />
                                        <span className="pr-version-count">{generations.length} versions for this date</span>
                                    </>
                                ) : (
                                    <span className="pr-version-single"><i className="pi pi-clock" aria-hidden="true" /> {generationTimestampLabel(reportDoc.generation_id) || `Version ${reportDoc.generation_id}`}</span>
                                )}
                            </div>
                        )}
                    </section>

                    {/* A PDF render this tab is waiting on takes precedence;
                        otherwise any in-flight run for this report date. */}
                    {pdfJob ? (
                        <GenerationProgress status={pdfJob} reportDate={pdfJob.report_date} style={{ marginBottom: '1rem' }} />
                    ) : isActivelyProcessing(dateLiveStatus(reportDoc.report_date)) && (
                        <GenerationProgress status={dateLiveStatus(reportDoc.report_date)} reportDate={reportDoc.report_date} style={{ marginBottom: '1rem' }} />
                    )}

                    {reportDoc.needs_review && !isSourcePlatform && (
                        <div className="pr-review-callout" role="note">
                            <i className="pi pi-exclamation-triangle" aria-hidden="true" />
                            <span>This version has at least one blocking data-quality issue - check the <strong>Needs Review</strong> tab before relying on or sharing it.</span>
                        </div>
                    )}

                    {isLendPlatform ? (
                        <LendDocTabs doc={reportDoc} onLoadingStart={beginLoading} onLoadingEnd={endLoading} />
                    ) : isMozzenoPlatform ? (
                        <MozzenoDocTabs doc={reportDoc} onLoadingStart={beginLoading} onLoadingEnd={endLoading} />
                    ) : (
                        <>
                            <ReportDocKpisSection docsByView={docsByView} typeOptions={typeOptions} />
                            <ReportDocTabs docsByView={docsByView} typeOptions={typeOptions} onDownloadExcludedLoans={handleDownloadExcludedLoans} key={reportDoc.generation_id} />
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default PlatformReport;
